import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import DataApi, { crmAuthHeaders } from '../../api/DataApi';
import {
  getApiErrorResponseData,
  parseApiToastHints,
} from '../../utils/apiErrorMessage';
import TelecmiApi from '../../api/TelecmiApi';
import {
  emitTelecmiOutboundFailed,
  emitTelecmiOutboundWaiting,
} from '../../utils/telecmiDialerBus';
import axios from 'axios';
import * as constants from '../../constants/CONSTANT';
import AppToast from '../AppToast';
import PathComponent from '../common/PathComponent';
import EmailButton from '../common/EmailButton';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import './css/DetailView.css';
import EditLayoutModal from './EditLayoutModal';
import DetailSingleFieldEditModal, {
  detailFieldSupportsQuickEdit,
} from './DetailSingleFieldEditModal';
import LeadConvertModal from './LeadConvertModal';
import RelatedTabsModal from './RelatedTabsModal';
import RelatedListView from './RelatedListView';
import WatConnectChat from '../watconnect/chat/WatConnectChat';
import OpportunityProductWizard from './OpportunityProductWizard';
import {
  canDeleteStandardRecord,
  canEditStandardRecord,
  useStandardRecordAccess,
} from '../../utils/standardRecordAccess';
import { pushRecentlyViewed } from '../../utils/recentlyViewed';
import QuoteProductWizard from './QuoteProductWizard';
import OrderProductWizard from './OrderProductWizard';
import { parseLineItemWizardConfig } from './lineItemWizardConfig';
import LayoutWidget from './LayoutWidget';
import {
  fetchUiComponentRegistryAllowlist,
  resolveRegisteredTabComponent,
} from './ComponentRegistry';
import { buildDetailCustomActionProps } from '../../utils/detailCustomActionProps';
import { isAdmin } from '../../utils/permissionCache';
import { getCustomScriptByIdOrKey, runCustomScriptPayload } from '../../api/CustomScriptApi';
import DynamicForm from './DynamicForm';
import OpportunityLineItemApi from '../../api/OpportunityLineItemApi';
import QuoteDocumentApi from '../../api/QuoteDocumentApi';
import GenerateDocumentApi from '../../api/GenerateDocumentApi';
import DocumentImageUploadModal from './DocumentImageUploadModal';
import OpportunityProductApi from '../../api/OpportunityProductApi';
import CurrencyApi from '../../api/CurrencyApi';
//import LayoutList from "./LayoutList";
import jwt_decode from 'jwt-decode';
import { buildTelecmiInitiatePayload } from '../../utils/telecmiInitiatePayload';
import { stashPendingRedirectToast } from '../../utils/pendingRedirectToast';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { isRecordviewObjectLookupField } from '../../utils/recordviewLookup';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import { Form, Dropdown, Card, Row, Col, ButtonGroup, Modal, Collapse, CloseButton } from 'react-bootstrap';
import { FaPlay, FaDownload } from 'react-icons/fa';
import { theme } from '../../theme/theme.js';
import { isFieldVisible } from '../../utils/taskFieldVisibility';
import {
  normalizeLeadConvertStatusFromLeadConversionConfig,
  leadStatusMatchesLeadConvertStatus,
} from '../objectmanager/leadFieldMappingUtils';
import { isMailConfigFieldVisible } from '../../utils/mailConfigFieldVisibility';
import { formatDate, formatDateTime, formatTime, formatNumber, formatCurrency, formatPercentFieldValue, formatCalculatedValue, formatCalculatedFieldForDisplay, normalizeCalculatedDisplayConfig, decodeHtmlEntitiesForDisplay } from '../../utils/formatUtils';
import { isFieldValueMasked, MASKED_FIELD_DISPLAY } from '../../utils/fieldConfigUtils';
import { resolveImageSrc } from '../../utils/resolveImageSrc';
import {
  buildExchangeRateLookupFromCurrencyList,
  resolveEffectiveExchangeRate,
} from '../../utils/reportCurrencyAggregate';
import { getMultiCurrencySettings } from '../../utils/multiCurrencySettings';
import TelecmiRecordingPlayerModal from '../common/TelecmiRecordingPlayerModal';
import { downloadTelecmiRecordingFile } from '../common/TelecmiRecordingAudio';
import { hasPackage } from '../../utils/entitlements';
// Import barcode and QR code libraries
import Barcode from 'react-barcode';
import QRCode from 'react-qr-code';

import AudienceModal from '../email/AudienceModal';
// Dummy layout data

const ensurePlainObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return { ...value };
};

const parseDisplayConfig = (displayConfig) => {
  if (!displayConfig) {
    return {};
  }

  if (typeof displayConfig === 'string') {
    try {
      const parsed = JSON.parse(displayConfig);
      return ensurePlainObject(parsed);
    } catch (error) {
      console.warn('Failed to parse display_config JSON:', error);
      return {};
    }
  }

  return ensurePlainObject(displayConfig);
};

const flattenDisplayConfig = (displayConfig) => {
  const root = parseDisplayConfig(displayConfig);
  const calculated = ensurePlainObject(root.calculated);
  const merged = { ...root, ...calculated };
  delete merged.calculated;

  return {
    root,
    calculated,
    merged
  };
};

const DEFAULT_RELATED_LIST_ACTIONS = ['new', 'edit', 'clone', 'delete', 'bulk_delete', 'import', 'export'];

/** Only the root DetailView (not layout-embedded) subscribes and renders the lookup preview modal. */
const LOOKUP_PREVIEW_OPEN = 'ibs-lookup-preview-open';
const LOOKUP_PREVIEW_CLOSE = 'ibs-lookup-preview-close';

function dispatchLookupPreviewOpen(detail) {
  if (typeof window === 'undefined' || !detail?.objectName || !detail?.recordId) return;
  window.dispatchEvent(new CustomEvent(LOOKUP_PREVIEW_OPEN, { detail }));
}

function dispatchLookupPreviewClose() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(LOOKUP_PREVIEW_CLOSE));
}

const ACTION_CONFIG_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * view_page form ui_code: `submitFeedback` / `submit_feedback` on root or under `submit` —
 * set to `inline` (aliases: `modal`, `form`) to show submit notices inside the form instead of toasts.
 * Legacy: `inlineSubmitErrors` / `inline_submit_errors` enables the same behavior.
 */
function resolveViewPageSubmitFeedbackMode(cfg) {
  if (!cfg || typeof cfg !== 'object') return 'toast';
  const explicit =
    cfg.submitFeedback ??
    cfg.submit_feedback ??
    cfg.submit?.submitFeedback ??
    cfg.submit?.submit_feedback;
  if (explicit != null && String(explicit).trim() !== '') {
    const v = String(explicit).trim().toLowerCase();
    if (v === 'inline' || v === 'modal' || v === 'form') return 'inline';
    return 'toast';
  }
  if (
    cfg.inlineSubmitErrors === true ||
    cfg.inline_submit_errors === true ||
    cfg.submit?.inlineSubmitErrors === true ||
    cfg.submit?.inline_submit_errors === true
  ) {
    return 'inline';
  }
  return 'toast';
}

/** Normalize font size for AppToast `--app-toast-message-font-size` (string or number → px). */
function normalizeViewPageToastFontSize(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return `${raw}px`;
  const s = String(raw).trim();
  return s || null;
}

/**
 * Optional view_page `submit.submitToast` / `submitToast`: sticky/manual dismiss, delay, message font size.
 * Example: `{ "sticky": true, "messageFontSize": 14 }`
 */
function resolveViewPageSubmitToastExtras(cfg) {
  if (!cfg || typeof cfg !== 'object') return {};
  const st =
    cfg.submitToast ??
    cfg.submit_toast ??
    cfg.submit?.submitToast ??
    cfg.submit?.submit_toast;
  if (!st || typeof st !== 'object') return {};
  const sticky =
    st.sticky === true ||
    st.sticky === 1 ||
    String(st.sticky ?? '').toLowerCase() === 'true' ||
    st.manualClose === true ||
    String(st.manualClose ?? '').toLowerCase() === 'true';
  const fs = normalizeViewPageToastFontSize(
    st.messageFontSize ?? st.fontSize ?? st.message_font_size
  );
  const out = {};
  if (sticky) out.sticky = true;
  if (st.delay != null && !sticky) {
    const d = typeof st.delay === 'number' ? st.delay : Number(st.delay);
    if (Number.isFinite(d) && d > 0) out.delay = d;
  }
  if (fs) out.messageFontSize = fs;
  return out;
}

const normalizeTabActions = (actions) => {
  if (Array.isArray(actions) && actions.length > 0) {
    const normalized = actions
      .map(action => String(action).toLowerCase())
      .filter(action => DEFAULT_RELATED_LIST_ACTIONS.includes(action));

    return normalized.length > 0 ? Array.from(new Set(normalized)) : [...DEFAULT_RELATED_LIST_ACTIONS];
  }

  return [...DEFAULT_RELATED_LIST_ACTIONS];
};

/** Lead layouts: quote PDF uses from-lead API (creates/links quote, then renders quote template). */
async function runDetailLayoutDocumentGeneration(
  objectName,
  recordId,
  templateAction,
  createAttachment,
  { onUploadRequired, templateId = null } = {}
) {
  const baseObject = String(objectName || '').toLowerCase().split('__')[0];
  if (baseObject === 'lead') {
    await GenerateDocumentApi.generateQuoteFromLead(recordId, templateAction);
    return { needsUpload: false };
  }

  const analysis = await GenerateDocumentApi.analyzeDocumentImages(
    objectName,
    recordId,
    templateAction,
    { templateId }
  );
  const slots = analysis?.uploadSlots || [];

  if (slots.length > 0 && typeof onUploadRequired === 'function') {
    onUploadRequired({
      objectName,
      recordId,
      templateAction,
      templateId,
      createAttachment,
      slots,
    });
    return { needsUpload: true };
  }

  await GenerateDocumentApi.generateDocument(objectName, recordId, templateAction, createAttachment, {
    templateId,
  });
  return { needsUpload: false };
}

/** Interpolate {{recordId}}, {{id}}, {{objectName}} for custom detail actions from actions.target_url */
function interpolateDetailCustomActionUrl(template, objectName, recordId) {
  if (!template || typeof template !== 'string') {
    return '';
  }
  const oid = recordId != null ? String(recordId) : '';
  const on = objectName != null ? String(objectName) : '';
  return template
    .replace(/\{\{\s*recordId\s*\}\}/gi, oid)
    .replace(/\{\{\s*id\s*\}\}/gi, oid)
    .replace(/\{\{\s*objectName\s*\}\}/gi, on);
}

/** First non-empty trimmed string from candidates (URL value vs display on detail). */
function pickDetailUrlSource(...candidates) {
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return '';
}

/**
 * Ensure anchor href works in a new tab when users omit the scheme (e.g. www.example.com).
 */
function normalizeDetailUrlHref(raw) {
  const t = raw != null ? String(raw).trim() : '';
  if (!t) return '';
  if (/^mailto:/i.test(t)) return t;
  if (/^tel:/i.test(t)) return t;
  if (/^https?:\/\//i.test(t)) return t;
  if (/^\/\//.test(t)) return `https:${t}`;
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return t;
  if (/^(www\.[^\s]+|[^\s/]+\.[a-z]{2,}[^\s]*)$/i.test(t) || /^(www\.[^\s]+|[^\s/]+\.[a-z]{2,}\/)/i.test(t)) {
    return `https://${t.replace(/^\/+/, '')}`;
  }
  return t;
}

/**
 * Custom script return value: in-app navigation only (no open redirect).
 * Scripts set `redirectPath` to e.g. `/view/account/123`, `/listview/lead/`, or `redirectView: { objectName, recordId }`.
 */
function resolveCustomScriptRedirectUrl(payload) {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  if (payload.redirectPath != null && String(payload.redirectPath).trim() !== '') {
    const p = String(payload.redirectPath).trim();
    if (p.includes('://') || p.startsWith('//')) {
      return null;
    }
    if (p.startsWith('/view/')) {
      return p;
    }
    if (p.startsWith('/listview/')) {
      const rest = p.replace(/^\/listview\//i, '').replace(/\/+$/, '');
      if (rest && /^[a-z0-9_]+$/i.test(rest)) {
        return `/listview/${encodeURIComponent(rest)}/`;
      }
    }
    return null;
  }
  const rv = payload.redirectView;
  if (rv && typeof rv === 'object') {
    const on = rv.objectName != null ? String(rv.objectName).trim() : '';
    const rid = rv.recordId != null ? String(rv.recordId).trim() : '';
    if (on && rid && /^[a-z0-9_]+$/i.test(on)) {
      return `/view/${encodeURIComponent(on)}/${encodeURIComponent(rid)}`;
    }
  }
  return null;
}

/** Allowed `toastVariant` values from custom_script return (Bootstrap Toast `bg`). */
const CUSTOM_SCRIPT_TOAST_VARIANTS = new Set([
  'success',
  'danger',
  'warning',
  'info',
  'primary',
  'secondary',
]);

function normalizeCustomScriptToastVariant(payload) {
  if (!payload || typeof payload !== 'object' || payload.toastVariant == null) {
    return 'success';
  }
  const v = String(payload.toastVariant).toLowerCase().trim();
  return CUSTOM_SCRIPT_TOAST_VARIANTS.has(v) ? v : 'success';
}

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function DetailView({ objectName, id, socket, selectedWhatsAppSetting, embedOptions }) {
  const hideEmbedActionBar = embedOptions?.hideActionBar === true;
  const hideEmbedSideWidgets = embedOptions?.hideSideWidgets === true;
  const hideEmbedRelatedLists = embedOptions?.hideRelatedLists === true;
  const hideEmbedPath = embedOptions?.hidePath === true;
  const embedDisableEdit = embedOptions?.disableEdit === true;
  const isLayoutEmbedDetail = embedOptions?.isLayoutEmbed === true;
  const isEmbedTabActive = embedOptions?.isTabActive !== false;

  const [activeTab, setActiveTab] = useState('detailsTab');
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const [showTabsDropdown, setShowTabsDropdown] = useState(false);
  const tabsDropdownRef = useRef(null);
  const [recordData, setRecordData] = useState(null);
  const [objectConfig, setObjectConfig] = useState(null);
  /** null = not loaded; Set of API field names tracked for field history on this object */
  const [fieldHistoryTrackedFieldNames, setFieldHistoryTrackedFieldNames] = useState(null);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    variant: 'success',
    delay: undefined,
  });
  const [showLayoutManager, setShowLayoutManager] = useState(false);
  /**
   * Heavy tab panels (nested DetailView, custom registry tabs) mount only after first visit.
   * Related-list tabs are already lazy in RelatedTabsModal (active tab only).
   */
  const [lazyDetailTabContentKeys, setLazyDetailTabContentKeys] = useState(
    () => new Set()
  );
  /** Lazy-loaded related list rows per tab (key = tab.tabId / related_list_id) */
  const [lazyRelatedTabRecords, setLazyRelatedTabRecords] = useState({});
  const [lazyRelatedTabLoading, setLazyRelatedTabLoading] = useState({});
  const loadedRelatedTabCacheRef = useRef(new Set());
  const inFlightRelatedTabsRef = useRef(new Set());
  const recordDataRef = useRef(recordData);
  recordDataRef.current = recordData;
  const [baseCurrencyCode, setBaseCurrencyCode] = useState(null);
  const [exchangeRateLookup, setExchangeRateLookup] = useState(null);
  const [multiCurrencyEnabled, setMultiCurrencyEnabled] = useState(true);
  const [enableInlineEditing, setEnableInlineEditing] = useState(true);
  const [openParentInModal, setOpenParentInModal] = useState(false);

  const [showLeadConvertModal, setShowLeadConvertModal] = useState(false);
  const [showAddAudience, setShowAddAudience] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const standardRecordAccess = useStandardRecordAccess();
  const canEditStandardRecordFlag = canEditStandardRecord(
    recordData,
    standardRecordAccess
  );
  const canDeleteStandardRecordFlag = canDeleteStandardRecord(
    recordData,
    standardRecordAccess
  );
  /** Field API name for single-field edit popup (pencil on detail row) */
  const [singleFieldEditName, setSingleFieldEditName] = useState(null);
  /** Set of `sectionName::index` keys for collapsed detail layout sections (default: expanded). */
  const [collapsedDetailSections, setCollapsedDetailSections] = useState(() => new Set());
  /**
   * Detail field visibility for the Blank Fields toggle:
   * all → hide_blank → show_blank → all
   */
  const [blankFieldsMode, setBlankFieldsMode] = useState('all');
  /**
   * Lookup field: preview related record in modal (root DetailView only — see LOOKUP_PREVIEW_* events).
   * Shape: { objectName, recordId } (no display name in chrome).
   */
  const [lookupPreviewModal, setLookupPreviewModal] = useState(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showNewRecordModal, setShowNewRecordModal] = useState(false);
  // Show WhatsApp chat by default for Lead and Contact objects (will be updated when record data loads)
  const [showWhatsAppChat, setShowWhatsAppChat] = useState(false);

  const [whatsAppRecipientPhone, setWhatsAppRecipientPhone] = useState('');
  const [updatingPathField, setUpdatingPathField] = useState(false);
  const [showProductWizard, setShowProductWizard] = useState(false);
  const [showQuoteProductWizard, setShowQuoteProductWizard] = useState(false);
  const [showOrderProductWizard, setShowOrderProductWizard] = useState(false);
  const [existingLineItems, setExistingLineItems] = useState([]);
  const [loadingLineItems, setLoadingLineItems] = useState(false);
  const [generatingDocument, setGeneratingDocument] = useState(null); // Track which button is generating (by index)
  const [docImageUploadModal, setDocImageUploadModal] = useState(null);
  const [docImageUploadGenerating, setDocImageUploadGenerating] = useState(false);

  useEffect(() => {
    if (isLayoutEmbedDetail && !isEmbedTabActive) {
      setDocImageUploadModal(null);
      setDocImageUploadGenerating(false);
      setGeneratingDocument(null);
    }
  }, [isLayoutEmbedDetail, isEmbedTabActive]);
  const [executingCustomScriptId, setExecutingCustomScriptId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteResultMessage, setDeleteResultMessage] = useState(null);
  const [deleteResultVariant, setDeleteResultVariant] = useState('danger');
  const [telecmiPlayer, setTelecmiPlayer] = useState({ show: false, filename: null });
  /** Server schedules onUpdate workflows after commit; a delayed fetch picks up those writes. */
  const asyncWorkflowFollowupRefreshTimerRef = useRef(null);
  const [isDeleting, setIsDeleting] = useState(false);
  /** @type {'cascade' | 'unlink'} */
  const [relatedRecordsDeleteMode, setRelatedRecordsDeleteMode] = useState('unlink');
  const [syncingProducts, setSyncingProducts] = useState(false);
  const [showRelatedListViewModal, setShowRelatedListViewModal] = useState(false);
  const [currentWidgetTab, setCurrentWidgetTab] = useState(null);
  const tabsContainerRef = useRef(null);
  const [maxVisibleTabs, setMaxVisibleTabs] = useState(10);
  // Stable tab overflow calc: measure tabs offscreen, then decide which fit.
  const tabsMeasureRef = useRef(null);
  const tabsMoreMeasureRef = useRef(null);
  const tabMeasureButtonRefs = useRef(new Map());
  /** Mount sidebar LayoutWidgets after first paint so detail column appears first */
  const [layoutWidgetsMountReady, setLayoutWidgetsMountReady] = useState(false);
  /** From component_registry; null = no allowlist (allow all keys in ComponentRegistry) */
  const [uiTabComponentAllowlist, setUiTabComponentAllowlist] = useState(null);
  const [customDetailActionFrame, setCustomDetailActionFrame] = useState({
    show: false,
    url: '',
    title: '',
  });
  /** Header custom action with action_behavior component */
  const [customDetailActionComponent, setCustomDetailActionComponent] = useState({
    show: false,
    title: '',
    Component: null,
    componentProps: null,
  });

  const [webComponentModal, setWebComponentModal] = useState({
    show: false,
    loading: false,
    submitting: false,
    formConfig: null,
    loadError: null,
    /** When form sets submitFeedback inline (see DynamicForm / view_page ui_code), messages show here instead of toasts. */
    submitFeedbackBanner: null,
    formKey: 0,
    modalTitle: '',
    result: null,
    /** Resolved action.config.propMappings for view_page (parent record fields, static text). */
    resolvedViewPageProps: null,
  });
  const [loadingWebComponentActionId, setLoadingWebComponentActionId] = useState(null);

  /** Inline field edit (pencil) only when this layout’s header includes an Edit action */
  const detailLayoutIncludesEditAction = useMemo(() => {
    const actions = recordData?.header?.actions;
    if (!Array.isArray(actions)) return false;
    return actions.some((a) => String(a?.action ?? '').toLowerCase() === 'edit');
  }, [recordData?.header?.actions]);

  // Root detail only: one lookup preview modal; embedded DetailViews dispatch open/close only.
  useEffect(() => {
    if (isLayoutEmbedDetail) {
      return;
    }
    const onOpen = (e) => {
      const d = e?.detail;
      if (d && d.objectName != null && d.recordId != null) {
        setLookupPreviewModal({
          objectName: String(d.objectName).trim(),
          recordId: String(d.recordId).trim(),
        });
      }
    };
    const onClose = () => setLookupPreviewModal(null);
    window.addEventListener(LOOKUP_PREVIEW_OPEN, onOpen);
    window.addEventListener(LOOKUP_PREVIEW_CLOSE, onClose);
    return () => {
      window.removeEventListener(LOOKUP_PREVIEW_OPEN, onOpen);
      window.removeEventListener(LOOKUP_PREVIEW_CLOSE, onClose);
    };
  }, [isLayoutEmbedDetail]);

  useEffect(() => {
    setCollapsedDetailSections(new Set());
    setWhatsAppRecipientPhone('');
    setBlankFieldsMode('all');
  }, [objectName, id]);

  const getRecordFieldValue = useCallback(
    (fieldName) => {
      if (!recordData || !fieldName) {
        return null;
      }

      /** List/grid sometimes send field-shaped objects { value, label, type, ... } instead of primitives */
      const unwrapScalar = (val) => {
        if (val == null || val === '') return null;
        if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
          const inner = val.value ?? val.displayValue;
          if (inner == null || inner === '') return null;
          if (typeof inner === 'object' && inner !== null && !Array.isArray(inner)) {
            return inner.value ?? inner.displayValue ?? null;
          }
          return inner;
        }
        return val;
      };

      const normalizedName = String(fieldName).toLowerCase();
      const highlightFields = Array.isArray(recordData?.highlightSection)
        ? recordData.highlightSection
        : [];
      const detailFields = (recordData?.details || []).flatMap(section => section.fields || []);
      // otherHiddenFields are not rendered in this layout; include here only for value resolution (e.g. currency).
      const hiddenFields = recordData?.otherHiddenFields || [];
      const allFields = [...highlightFields, ...detailFields, ...hiddenFields];

      const match = allFields.find(
        field => field?.fieldName?.toLowerCase() === normalizedName
      );

      const raw = match?.value ?? null;
      if (normalizedName === 'exchange_rate' || normalizedName === 'currency') {
        return unwrapScalar(raw);
      }
      return raw;
    },
    [recordData]
  );

  const getRecordFieldLabel = useCallback(
    (fieldName) => {
      if (!recordData || !fieldName) return null;
      const normalizedName = String(fieldName).toLowerCase();
      const highlightFields = Array.isArray(recordData?.highlightSection)
        ? recordData.highlightSection
        : [];
      const detailFields = (recordData?.details || []).flatMap((section) => section.fields || []);
      const hiddenFields = recordData?.otherHiddenFields || [];
      const allFields = [...highlightFields, ...detailFields, ...hiddenFields];
      const match = allFields.find(
        (field) => field?.fieldName?.toLowerCase() === normalizedName
      );
      return match?.label ?? match?.fieldLabel ?? match?.field_label ?? null;
    },
    [recordData]
  );

  const defaultWhatsAppRecipientName = useMemo(() => {
    const first =
      recordData?.details
        ?.flatMap((s) => s.fields)
        ?.find((f) => f.fieldName === 'first_name')?.value || '';
    const last =
      recordData?.details
        ?.flatMap((s) => s.fields)
        ?.find((f) => f.fieldName === 'last_name')?.value || '';
    return `${first} ${last}`.trim();
  }, [recordData]);

  const defaultWhatsAppRecipientPhone = useMemo(() => {
    return (
      recordData?.details
        ?.flatMap((s) => s.fields)
        ?.find((f) => f.fieldName === 'phone')?.value || ''
    );
  }, [recordData]);

  const resolveWhatsAppCandidatesFromAction = useCallback(
    (action) => {
      const configured =
        action?.phone_fields ??
        action?.phoneFields ??
        action?.fields ??
        action?.field_names ??
        action?.fieldNames;
      const list = Array.isArray(configured) ? configured : [];
      const out = [];
      for (const fieldName of list) {
        const v = getRecordFieldValue(fieldName);
        if (v == null || String(v).trim() === '') continue;
        out.push({
          fieldName: String(fieldName),
          label: getRecordFieldLabel(fieldName) || String(fieldName),
          value: String(v),
        });
      }
      return out;
    },
    [getRecordFieldLabel, getRecordFieldValue]
  );

  /** Raw picklist/path values to compare to Lead Convert Status (leadstatus, path fields, header). */
  const getLeadStatusRawCandidates = useCallback(() => {
    const list = [];
    const add = (v) => {
      if (v == null || v === '') return;
      list.push(v);
    };

    add(getRecordFieldValue('leadstatus'));
    add(getRecordFieldValue('lead_status'));

    const h = recordData?.header;
    if (h?.leadstatus != null && h.leadstatus !== '') add(h.leadstatus);
    if (h?.lead_status != null && h.lead_status !== '') add(h.lead_status);

    const highlightFields = Array.isArray(recordData?.highlightSection)
      ? recordData.highlightSection
      : [];
    const detailFields = (recordData?.details || []).flatMap((section) => section.fields || []);
    const hiddenFields = recordData?.otherHiddenFields || [];
    for (const field of [...highlightFields, ...detailFields, ...hiddenFields]) {
      if (field?.is_path_field === true) {
        add(field.value);
      }
    }

    return list;
  }, [getRecordFieldValue, recordData]);

  /** Value to match on the child list (record id for id-based relationships, or e.g. phone for phone-based) */
  const getRelationshipFilterValue = useCallback(
    (sourceFieldName) => {
      if (!sourceFieldName || String(sourceFieldName).toLowerCase() === 'id') {
        return id;
      }
      const raw = getRecordFieldValue(sourceFieldName);
      if (raw == null || raw === '') {
        return null;
      }
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        const inner = raw.value ?? raw.displayValue;
        if (inner == null || inner === '') {
          return null;
        }
        if (typeof inner === 'object' && inner !== null) {
          return String(inner.value ?? inner.displayValue ?? '');
        }
        return String(inner);
      }
      return String(raw);
    },
    [id, getRecordFieldValue]
  );

  /** Lookup / id field on the current record as a string id for nested detail (uses visible + hidden layout fields). */
  const resolveFieldAsRecordId = useCallback(
    (fieldName) => {
      if (!fieldName || String(fieldName).toLowerCase() === 'id') {
        return id != null && String(id).trim() !== '' ? String(id).trim() : null;
      }
      const raw = getRecordFieldValue(fieldName);
      if (raw == null || raw === '') {
        return null;
      }
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
        const inner = raw.value ?? raw.displayValue;
        if (inner == null || inner === '') {
          return null;
        }
        if (typeof inner === 'object' && inner !== null) {
          const v = inner.value ?? inner.displayValue;
          return v != null && String(v).trim() !== '' ? String(v).trim() : null;
        }
        return String(inner).trim() || null;
      }
      const s = String(raw).trim();
      return s || null;
    },
    [id, getRecordFieldValue]
  );

  // Get name field value from details section
  const getNameFromDetails = useCallback(() => {
    const pick = (fieldName) => {
      const v = getRecordFieldValue(fieldName);
      if (v == null || v === '') return null;
      const s = String(v).trim();
      return s || null;
    };

    if (recordData?.header?.titleField) {
      const fromHeader = pick(recordData.header.titleField);
      if (fromHeader) return fromHeader;
    }

    const name = pick('name');
    if (name) return name;

    const personName = [pick('first_name'), pick('lastname') || pick('last_name')]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (personName) return personName;

    return pick('company') || pick('subject') || pick('title') || null;
  }, [recordData, getRecordFieldValue]);

  useEffect(() => {
    if (!objectName || !id || !recordData) return;
    const title = getNameFromDetails() || String(id);
    pushRecentlyViewed({
      id,
      objectName,
      objectLabel: objectConfig?.object_label || objectName,
      title,
      href: `/view/${objectName}/${id}`,
    });
  }, [objectName, id, recordData, objectConfig, getNameFromDetails]);

  // Get currency from record only (no fallback) - used for opportunity/quote product selection
  const getRecordCurrencyOnly = useCallback(() => {
    return getRecordFieldValue('currency') || null;
  }, [getRecordFieldValue]);

  // Centralized token decoding - decode once and reuse
  const decodedToken = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        return jwt_decode(token);
      }
    } catch (error) {
      console.error('Error decoding token:', error);
    }
    return null;
  }, []);

  // Resolve currency with priority: record currency > token currency (user currency or company currency) > default
  const resolveCurrency = useCallback((fallbackCurrency = 'INR') => {
    // Priority 1: Check currency field on current object (record)
    const recordCurrency = getRecordFieldValue('currency');
    if (recordCurrency) {
      return recordCurrency;
    }

    // Priority 2: Check currency from JWT token
    // Token currency is COALESCE(u.currency, c.currency) from auth.model.js
    // So it will be user.currency if available, otherwise company.currency
    if (decodedToken?.currency) {
      return decodedToken.currency;
    }

    // Fallback to default
    return fallbackCurrency;
  }, [getRecordFieldValue, decodedToken]);

  // Get enable_watconnect from JWT token
  const enableWatConnect = useMemo(() => {
    // WhatsApp action should appear only when WatConnect package is enabled.
    // Some environments don't stamp enable_watconnect into the JWT; entitlements is the source of truth.
    return decodedToken?.enable_watconnect === true || hasPackage('watconnect');
  }, [decodedToken]);

  // Debug: why WhatsApp action/button is missing.
  useEffect(() => {
    const actions = recordData?.header?.actions;
    if (!Array.isArray(actions)) return;
    const hasWa =
      actions.some((a) => {
        const key =
          typeof a === 'string'
            ? a
            : (a?.action ?? a?.type ?? '');
        return String(key).toLowerCase() === 'whatsapp';
      });
    if (hasWa) {
      console.log('[DetailLayout][WhatsApp][debug] header.actions includes whatsapp', {
        objectName,
        id,
        enableWatConnect,
        actions,
      });
    } else {
      console.log('[DetailLayout][WhatsApp][debug] header.actions has no whatsapp', {
        objectName,
        id,
        enableWatConnect,
        actions,
      });
    }
  }, [recordData?.header?.actions, objectName, id, enableWatConnect]);

  const enableTelecmi = useMemo(() => {
    return decodedToken?.enable_telecmi === true;
  }, [decodedToken]);
  const enableCallyzer = useMemo(() => {
    return decodedToken?.enable_callyzer === true;
  }, [decodedToken]);
  const enableVgrowDialer = useMemo(() => {
    return decodedToken?.enable_vgrow_dialer === true;
  }, [decodedToken]);
  const enableCti = useMemo(() => {
    return enableTelecmi || enableCallyzer || enableVgrowDialer;
  }, [enableTelecmi, enableCallyzer, enableVgrowDialer]);

  // Base currency + current org exchange rates (fallback when record exchange_rate is missing)
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const currencyApi = new CurrencyApi();
        const res = await currencyApi.fetchAll();
        const raw = res?.data?.data ?? res?.data;
        const list = Array.isArray(raw) ? raw : [];
        if (cancelled) return;
        const baseRow = list.find((r) => r.is_base_currency === true);
        setBaseCurrencyCode(baseRow?.currency_code ? String(baseRow.currency_code).trim() : null);
        setExchangeRateLookup(buildExchangeRateLookupFromCurrencyList(list));
      } catch (e) {
        if (!cancelled) {
          setBaseCurrencyCode(null);
          setExchangeRateLookup(null);
        }
      }
      try {
        const s = await getMultiCurrencySettings();
        if (!cancelled) {
          setMultiCurrencyEnabled(!!s.multiCurrencyEnabled);
          setEnableInlineEditing(!!s.enableInlineEditing);
          setOpenParentInModal(!!s.openParentInModal);
        }
      } catch {
        if (!cancelled) {
          setMultiCurrencyEnabled(true);
          setEnableInlineEditing(true);
          setOpenParentInModal(false);
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    (async () => {
      const allowlist = await fetchUiComponentRegistryAllowlist(ac.signal, objectName);
      if (!cancelled) setUiTabComponentAllowlist(allowlist);
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [objectName]);

  const displayBaseCurrencyCode = multiCurrencyEnabled ? baseCurrencyCode : null;
  const displayExchangeRateLookup = multiCurrencyEnabled ? exchangeRateLookup : null;

  const relatedTabsSource = useMemo(() => recordData?.tabs || [], [recordData?.tabs]);

  const normalizedRelatedTabs = useMemo(() => {
    if (!Array.isArray(relatedTabsSource)) {
      return [];
    }

    return relatedTabsSource.map(tab => {
      const config = tab?.config || {};
      const normalizedActions = normalizeTabActions(config.actions || tab?.actions);
      const tabId = tab?.tabId != null && tab.tabId !== ''
        ? tab.tabId
        : config.related_list_id;

      return {
        ...tab,
        tabId,
        actions: normalizedActions,
        config: {
          ...config,
          actions: normalizedActions,
        },
      };
    });
  }, [relatedTabsSource]);

  // For email_template / email_layout, do not show Email (email_log) related list tab
  const displayRelatedTabs = useMemo(() => {
    const obj = objectName?.toLowerCase();
    if (obj === 'email_template' || obj === 'email_layout') {
      return normalizedRelatedTabs.filter(t => t.config?.target_object_name?.toLowerCase() !== 'email_log');
    }
    return normalizedRelatedTabs;
  }, [objectName, normalizedRelatedTabs]);

  const displayRelatedTabsWithLazyRecords = useMemo(() => {
    return displayRelatedTabs.map((tab) => {
      const tid = tab.tabId != null ? String(tab.tabId) : null;
      if (tid != null && Object.prototype.hasOwnProperty.call(lazyRelatedTabRecords, tid)) {
        return { ...tab, records: lazyRelatedTabRecords[tid] };
      }
      return { ...tab, records: tab.records || [] };
    });
  }, [displayRelatedTabs, lazyRelatedTabRecords]);

  /** Related-list tabs + search-view layout widgets (same display_order as Related List Manager) */
  const detailTabsMerged = useMemo(() => {
    const searchWidgets = (recordData?.widgets || []).filter((w) => {
      if (String(w.widgetType || '').toLowerCase() !== 'search') return false;
      const cfg = w.config || {};
      return cfg.show_in_detail_tabs !== false;
    });

    const searchTabs = searchWidgets.map(w => {
      const cfg = w.config || {};
      const displayLabel = w.widgetLabel || cfg.search_view_name || 'Search';
      return {
        tabName: `__searchWidget_${w.widgetId}`,
        displayLabel,
        tabId: w.widgetId,
        records: [],
        config: {
          tab_kind: 'search_widget',
          display_order: w.displayOrder ?? 0,
          search_widget: {
            widgetId: w.widgetId,
            widgetType: w.widgetType,
            widgetLabel: w.widgetLabel,
            widgetColumnPosition: w.widgetColumnPosition,
            componentId: w.componentId,
            config: cfg,
            displayOrder: w.displayOrder,
            data: w.data
          }
        },
        _sortKey: w.displayOrder ?? 0
      };
    });

    const relatedPrepared = displayRelatedTabsWithLazyRecords.map(t => ({
      ...t,
      displayLabel: t.tabName,
      _sortKey: t.config?.display_order ?? 0
    }));

    return [...relatedPrepared, ...searchTabs]
      .sort((a, b) => a._sortKey - b._sortKey)
      .map(({ _sortKey, ...tab }) => tab);
  }, [displayRelatedTabsWithLazyRecords, recordData?.widgets]);

  /** Related / search-widget tabs omitted when embedded with hideRelatedLists */
  const detailTabsMergedForDisplay = useMemo(() => {
    if (hideEmbedRelatedLists) {
      return [];
    }
    return detailTabsMerged;
  }, [hideEmbedRelatedLists, detailTabsMerged]);

  const customTabs = useMemo(() => {
    const rawConfig = objectConfig?.config;
    if (!rawConfig) {
      return [];
    }

    let parsedConfig = rawConfig;
    if (typeof rawConfig === 'string') {
      try {
        parsedConfig = JSON.parse(rawConfig);
      } catch (error) {
        console.warn('Failed to parse object config JSON:', error);
        return [];
      }
    }

    if (!parsedConfig?.customTabs || !Array.isArray(parsedConfig?.tabs)) {
      return [];
    }

    return parsedConfig.tabs;
  }, [objectConfig]);

  const leadConvertStatusConfig = useMemo(() => {
    if (objectName?.toLowerCase() !== 'lead' || !objectConfig?.config) {
      return null;
    }
    let parsed = objectConfig.config;
    if (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        return null;
      }
    }
    return normalizeLeadConvertStatusFromLeadConversionConfig(parsed?.lead_conversion_config);
  }, [objectName, objectConfig]);

  /** Single wizard profile: `config.lineItemWizard` with fallbacks in lineItemWizardConfig.js */
  const lineItemWizardConfigForModal = useMemo(
    () => parseLineItemWizardConfig(objectConfig?.config),
    [objectConfig?.config]
  );

  const statusHidesLeadConvert = useMemo(() => {
    if (objectName?.toLowerCase() !== 'lead') return false;
    if (!leadConvertStatusConfig) return false;
    return getLeadStatusRawCandidates().some((raw) =>
      leadStatusMatchesLeadConvertStatus(raw, leadConvertStatusConfig)
    );
  }, [objectName, leadConvertStatusConfig, getLeadStatusRawCandidates]);

  const normalizedCustomTabs = useMemo(() => {
    const tabs = customTabs.map((tab, index) => {
      const label = tab?.label || tab?.component || `Tab ${index + 1}`;
      const tabName = `custom:${tab?.name || tab?.component || label || index}`;
      return {
        ...tab,
        tabName,
        label
      };
    });

    // Admin MFA recovery on Setup → Users (always available; no layout config required)
    try {
      const token = localStorage.getItem('token');
      const viewer = token ? jwt_decode(token) : null;
      if (
        String(objectName || '').toLowerCase() === 'user' &&
        isAdmin(viewer)
      ) {
        const exists = tabs.some(
          (t) =>
            t?.component === 'UserMfaAdminTab' ||
            t?.name === 'user_mfa_admin'
        );
        if (!exists) {
          tabs.push({
            type: 'component',
            component: 'UserMfaAdminTab',
            name: 'user_mfa_admin',
            label: 'MFA',
            tabName: 'custom:user_mfa_admin',
          });
        }
      }
    } catch (e) {
      // ignore token decode errors
    }

    return tabs;
  }, [customTabs, objectName]);

  /**
   * Tabs from object_layout_config.config (detail payload: layout_detail_config).
   * Each entry embeds another object's detail for the id stored in parent_field.
   */
  const layoutDetailEmbedTabs = useMemo(() => {
    const raw = recordData?.layout_detail_config;
    if (!raw) {
      return [];
    }
    let parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.tabs)) {
      return [];
    }
    return parsed.tabs
      .map((t, i) => (t && typeof t === 'object' ? t : null))
      .filter(
        t =>
          t &&
          String(t.parent_object || '').trim() &&
          String(t.parent_field || '').trim()
      )
      .map((t, i) => ({
        ...t,
        tab_name: String(t.tab_name || t.tabName || `layout_tab_${i}`).trim(),
        tab_label: String(
          t.tab_label || t.tabLabel || t.parent_object || `Tab ${i + 1}`
        ).trim(),
        parent_object: String(t.parent_object || '')
          .trim()
          .toLowerCase(),
        parent_field: String(t.parent_field || '').trim(),
        hide: Array.isArray(t.hide)
          ? t.hide.map(x => String(x).toLowerCase())
          : [],
        /** When false, nested detail is read-only (no inline edit / header Edit). */
        edit: t.edit !== false,
      }));
  }, [recordData?.layout_detail_config]);

  /**
   * When this DetailView is rendered inside another record's layout-config tab (object_layout_config.config tabs),
   * do not show this record's own layout embed tabs — avoids a nested tab strip (e.g. Lead under Counselled Detail).
   */
  const layoutDetailEmbedTabsForDisplay = useMemo(() => {
    if (isLayoutEmbedDetail) {
      return [];
    }
    return layoutDetailEmbedTabs;
  }, [isLayoutEmbedDetail, layoutDetailEmbedTabs]);

  /** Embedded detail with only the main fields panel: omit redundant "Details" tab row */
  const detailPanelTabCount = useMemo(() => {
    const customComponentTabCount = normalizedCustomTabs.filter(
      (t) => t?.type === 'component'
    ).length;
    return (
      1 +
      customComponentTabCount +
      layoutDetailEmbedTabsForDisplay.length +
      detailTabsMergedForDisplay.length
    );
  }, [
    normalizedCustomTabs,
    layoutDetailEmbedTabsForDisplay,
    detailTabsMergedForDisplay,
  ]);
  const hideEmbedDetailsTabBar =
    isLayoutEmbedDetail && detailPanelTabCount === 1;

  /** Header breadcrumb: use object_config label (human-readable), not API name from view payload */
  const detailPageObjectLabel = useMemo(() => {
    const fromConfig = objectConfig?.object_label && String(objectConfig.object_label).trim();
    if (fromConfig) return fromConfig;
    const fromHeader = recordData?.header?.objectLabel && String(recordData.header.objectLabel).trim();
    if (fromHeader) return fromHeader;
    if (objectName) return objectName;
    return '—';
  }, [objectConfig?.object_label, recordData?.header?.objectLabel, objectName]);

  // Get current user from JWT token
  const getCurrentUser = useCallback(() => {
    if (decodedToken) {
      const user = {
        id: decodedToken.id,
        firstname: decodedToken.firstname,
        lastname: decodedToken.lastname,
        email: decodedToken.email,
        username: decodedToken.username,
      };
      console.log('Current user:', user);
      return user;
    }
    const defaultUser = {
      id: 'current-user-id',
      firstname: 'Current',
      lastname: 'User',
      email: 'current.user@company.com',
    };
    console.log('Using default user:', defaultUser);
    return defaultUser;
  }, [decodedToken]);

  const getBestPhoneNumber = useCallback(() => {
    const candidates = ['phone', 'mobile', 'mobile_no', 'mobile_number', 'contact_no', 'phone_number'];
    for (const fieldName of candidates) {
      const value = getRecordFieldValue(fieldName);
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  }, [getRecordFieldValue]);

  const handleTelecmiCall = useCallback(async () => {
    if (!enableCti) {
      setToast({
        show: true,
        message: 'CTI solution is not enabled for your organization.',
        variant: 'danger',
      });
      return;
    }
    const phone = getBestPhoneNumber();
    if (!phone) {
      setToast({
        show: true,
        message: 'No phone number available on this record.',
        variant: 'danger',
      });
      return;
    }

    const toNum = Number(String(phone).replace(/\D/g, ''));
    const built = buildTelecmiInitiatePayload(toNum, { crm: true });
    if (built.error) {
      setToast({
        show: true,
        message: built.error,
        variant: 'danger',
      });
      return;
    }
    emitTelecmiOutboundWaiting({ to: toNum });
    try {
      const response = await TelecmiApi.initiateCall(built.payload);
      const message =
        response?.data?.data?.msg ||
        response?.data?.msg ||
        response?.data?.message ||
        response?.data?.data?.message ||
        'Call initiated successfully.';
      setToast({
        show: true,
        message,
        variant: 'success',
      });
    } catch (error) {
      const message =
        error?.response?.data?.data?.msg ||
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to initiate call.';
      emitTelecmiOutboundFailed({ message, to: toNum });
      setToast({
        show: true,
        message,
        variant: 'danger',
      });
    }
  }, [getBestPhoneNumber, enableCti]);

  const handleTelecmiCallForNumber = useCallback(async (phoneValue) => {
    if (!enableCti) {
      setToast({
        show: true,
        message: 'CTI solution is not enabled for your organization.',
        variant: 'danger',
      });
      return;
    }
    const normalized = String(phoneValue || '').replace(/\D/g, '');
    if (!normalized) {
      setToast({
        show: true,
        message: 'Invalid phone number.',
        variant: 'danger',
      });
      return;
    }

    const toNum = Number(normalized);
    const built = buildTelecmiInitiatePayload(toNum, { crm: true });
    if (built.error) {
      setToast({
        show: true,
        message: built.error,
        variant: 'danger',
      });
      return;
    }
    emitTelecmiOutboundWaiting({ to: toNum });
    try {
      const response = await TelecmiApi.initiateCall(built.payload);
      const message =
        response?.data?.data?.msg ||
        response?.data?.msg ||
        response?.data?.message ||
        response?.data?.data?.message ||
        'Call initiated successfully.';
      setToast({
        show: true,
        message,
        variant: 'success',
      });
    } catch (error) {
      const message =
        error?.response?.data?.data?.msg ||
        error?.response?.data?.msg ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to initiate call.';
      emitTelecmiOutboundFailed({ message, to: toNum });
      setToast({
        show: true,
        message,
        variant: 'danger',
      });
    }
  }, [enableCti]);

  // Close dropdown when clicking outside and update position on scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on the portal dropdown
      const portalDropdown = document.querySelector('.tabs-dropdown-menu');
      if (tabsDropdownRef.current && !tabsDropdownRef.current.contains(event.target) &&
        (!portalDropdown || !portalDropdown.contains(event.target))) {
        setShowTabsDropdown(false);
      }
    };

    const handleScroll = () => {
      // Close dropdown on scroll to avoid positioning issues
      if (showTabsDropdown) {
        setShowTabsDropdown(false);
      }
    };

    if (showTabsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [showTabsDropdown]);

  // Handle "Show All" from widget
  const handleWidgetShowAll = (tabObject, currentRecordId, sourceObjectName) => {
    console.log('🔍 Opening related list view modal from widget:', tabObject);
    console.log('🔍 Current record ID:', currentRecordId);
    console.log('🔍 Source object name:', sourceObjectName);

    // Validate that we have a valid object name
    const objectName = tabObject.config?.target_object_name;
    if (!objectName) {
      console.error('❌ Missing target_object_name in tab config:', tabObject);
      setToast({
        show: true,
        message: 'Error: No target object name found for this related list.',
        variant: 'danger',
      });
      return;
    }

    // Validate that we have target_field_name for filtering related records
    const targetFieldName = tabObject.config?.target_field_name;
    if (!targetFieldName) {
      console.error('❌ Missing target_field_name in tab config. Cannot filter related records.');
      setToast({
        show: true,
        message: 'Error: Cannot determine relationship field. Related records cannot be filtered.',
        variant: 'danger',
      });
      return;
    }

    console.log('✅ Relationship info:', {
      target_object_name: objectName,
      target_field_name: targetFieldName,
      parentId: currentRecordId,
      sourceObjectName: sourceObjectName
    });

    // Store the current tab in window object for RelatedListView to access
    // This is required for RelatedListView to get field config and other settings
    window.currentModalTab = {
      ...tabObject,
      config: {
        ...tabObject.config,
        target_field_name: targetFieldName, // Ensure target_field_name is set for filtering
        // Preserve field_config if it exists (contains fields to display)
        field_config: tabObject.config?.field_config || undefined
      }
    };

    setCurrentWidgetTab(tabObject);
    setShowRelatedListViewModal(true);
  };

  const fetchExistingLineItems = useCallback(async () => {
    if (!id) {
      console.warn('Cannot load opportunity products: missing opportunity id');
      return;
    }
    setLoadingLineItems(true);
    try {
      console.log('Fetching existing opportunity products for', id);
      const response = await OpportunityLineItemApi.fetchByOpportunity(id);
      console.log('Existing line items response:', response);
      if (response?.success) {
        setExistingLineItems(response.data || []);
      } else {
        setExistingLineItems([]);
      }
    } catch (error) {
      console.error('Failed to fetch opportunity products:', error);
      setExistingLineItems([]);
    } finally {
      setLoadingLineItems(false);
    }
  }, [id]);

  const handleAddProducts = async () => {
    const currency = getRecordCurrencyOnly();
    if (!currency) {
      setToast({
        show: true,
        message: 'Please set a currency on this opportunity before adding line items.',
        variant: 'danger',
      });
      return;
    }
    await fetchExistingLineItems();
    setShowProductWizard(true);
  };

  const handleAddQuoteProducts = () => {
    const currency = getRecordCurrencyOnly();
    if (!currency) {
      setToast({
        show: true,
        message: 'Please set a currency on this quote before adding line items.',
        variant: 'danger',
      });
      return;
    }
    setShowQuoteProductWizard(true);
  };

  const handleAddOrderProducts = () => {
    const currency = getRecordCurrencyOnly();
    if (!currency) {
      setToast({
        show: true,
        message: 'Please set a currency on this order before adding line items.',
        variant: 'danger',
      });
      return;
    }
    setShowOrderProductWizard(true);
  };

  const handleProductWizardClose = () => {
    setShowProductWizard(false);
  };
  const handleQuoteProductWizardClose = () => {
    setShowQuoteProductWizard(false);
  };

  const handleOrderProductWizardClose = () => {
    setShowOrderProductWizard(false);
  };

  const handleProductWizardSaveSuccess = () => {
    setToast({
      show: true,
      message: 'Products added successfully!',
      variant: 'success',
    });
    refreshRecordData();
  };
  const handleQuoteProductWizardSaveSuccess = () => {
    setToast({
      show: true,
      message: 'Quote products saved successfully!',
      variant: 'success',
    });
    refreshRecordData();
  };

  const handleOrderProductWizardSaveSuccess = () => {
    setToast({
      show: true,
      message: 'Order products saved successfully!',
      variant: 'success',
    });
    refreshRecordData();
  };

  const handleSyncProducts = async () => {
    if (!id) {
      setToast({
        show: true,
        message: 'Quote ID is missing.',
        variant: 'danger',
      });
      return;
    }

    // Get opportunity ID from quote record
    const opportunityId = getRecordFieldValue('opportunity');
    if (!opportunityId) {
      setToast({
        show: true,
        message: 'This quote is not linked to an opportunity. Please link an opportunity first.',
        variant: 'warning',
      });
      return;
    }

    setSyncingProducts(true);
    try {
      const response = await OpportunityProductApi.syncProductsFromQuote({
        quoteId: id,
        opportunityId: opportunityId,
      });

      if (response?.success) {
        setToast({
          show: true,
          message: response.message || `Successfully synced: ${response.added || 0} added, ${response.updated || 0} updated, ${response.removed || 0} removed`,
          variant: 'success',
        });
        refreshRecordData();
      } else {
        setToast({
          show: true,
          message: response?.message || 'Failed to sync products',
          variant: 'danger',
        });
      }
    } catch (error) {
      console.error('Error syncing products:', error);
      setToast({
        show: true,
        message: error?.message || 'Failed to sync products',
        variant: 'danger',
      });
    } finally {
      setSyncingProducts(false);
    }
  };

  const handleDownloadQuoteDocument = async () => {
    if (!id) {
      setToast({
        show: true,
        message: 'Quote ID is missing.',
        variant: 'danger',
      });
      return;
    }

    try {
      setToast({
        show: true,
        message: 'Generating quote document...',
        variant: 'info',
      });

      await QuoteDocumentApi.downloadQuoteDocument(id, 'customer quote', 'html');

      setToast({
        show: true,
        message: 'Quote document downloaded successfully!',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error downloading quote document:', error);
      setToast({
        show: true,
        message: error.message || 'Failed to download quote document. Please ensure a document template is configured.',
        variant: 'danger',
      });
    }
  };

  const handleGenerateDocument = async () => {
    if (!id) {
      setToast({
        show: true,
        message: 'Record ID is missing.',
        variant: 'danger',
      });
      return;
    }

    try {
      setToast({
        show: true,
        message: 'Generating document...',
        variant: 'info',
      });

      await GenerateDocumentApi.generateDocument(objectName, id, 'customer quote');

      setToast({
        show: true,
        message: 'Document generated and downloaded successfully!',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error generating document:', error);
      setToast({
        show: true,
        message: error.message || 'Failed to generate quote document. Please ensure a document template is configured.',
        variant: 'danger',
      });
    }
  };

  const executeDetailLayoutDocumentGeneration = useCallback(
    async (templateAction, createAttachment, actionIdx, actionLabel, templateId = null) => {
      return runDetailLayoutDocumentGeneration(objectName, id, templateAction, createAttachment, {
        templateId,
        onUploadRequired: (payload) => {
          setGeneratingDocument(null);
          setDocImageUploadModal({
            ...payload,
            actionIdx,
            actionLabel: actionLabel || 'Document',
          });
        },
      });
    },
    [objectName, id]
  );

  const handleDocImageUploadConfirm = useCallback(
    async ({ files, uploadSessionId }) => {
      if (!docImageUploadModal) return;

      const {
        objectName: modalObjectName,
        recordId,
        templateAction,
        templateId,
        createAttachment,
        actionLabel,
      } = docImageUploadModal;

      const hasFiles = files && typeof files === 'object' && Object.keys(files).length > 0;

      try {
        setDocImageUploadGenerating(true);
        setGeneratingDocument(null);

        let uploadImages = {};
        let sessionId = uploadSessionId;

        if (hasFiles) {
          setToast({
            show: true,
            message: 'Uploading images...',
            variant: 'primary',
          });

          const uploaded = await GenerateDocumentApi.uploadDocumentImages(
            modalObjectName,
            recordId,
            files,
            uploadSessionId
          );
          uploadImages = uploaded.uploadImages || {};
          sessionId = uploaded.uploadSessionId || uploadSessionId;
        }

        setToast({
          show: true,
          message: `Generating ${actionLabel || 'Document'}...`,
          variant: 'primary',
        });

        await GenerateDocumentApi.generateDocumentWithUploads(
          modalObjectName,
          recordId,
          templateAction,
          createAttachment,
          uploadImages,
          { uploadSessionId: sessionId, templateId }
        );

        setDocImageUploadModal(null);
        setToast({
          show: true,
          message: `${actionLabel || 'Document'} generated and downloaded successfully!${createAttachment ? ' Document has been attached to this record.' : ''}`,
          variant: 'success',
        });
      } catch (error) {
        console.error('Error generating document with uploads:', error);
        setToast({
          show: true,
          message: error.message || 'Failed to generate document. Please ensure a document template is configured.',
          variant: 'danger',
        });
      } finally {
        setDocImageUploadGenerating(false);
      }
    },
    [docImageUploadModal]
  );

  // Handle delete record
  const handleDeleteRecord = () => {
    if (!id) {
      setToast({
        show: true,
        message: 'Record ID is missing.',
        variant: 'danger',
      });
      return;
    }
    setDeleteResultMessage(null);
    setDeleteResultVariant('danger');
    setRelatedRecordsDeleteMode('unlink');
    setShowDeleteModal(true);
  };

  const handleDeleteModalHide = () => {
    const wasSuccessful =
      deleteResultMessage && deleteResultVariant === 'success';
    setShowDeleteModal(false);
    setDeleteResultMessage(null);
    setDeleteResultVariant('danger');
    setRelatedRecordsDeleteMode('unlink');
    if (wasSuccessful) {
      window.location.href = `/listview/${objectName}`;
    }
  };

  const confirmDeleteRecord = async () => {
    if (!id) return;

    setIsDeleting(true);
    setDeleteResultMessage(null);

    try {
      const dataApi = new DataApi('object');
      const params = new URLSearchParams();
      if (relatedRecordsDeleteMode === 'cascade') {
        params.set('cascadeDeleteChildren', 'true');
      } else {
        params.set('unlinkReferencesBeforeDelete', 'true');
      }
      const qs = params.toString();
      const response = await dataApi.delete(
        `/${objectName}/${id}${qs ? `?${qs}` : ''}`
      );

      if (response?.data?.success) {
        setDeleteResultVariant('success');
        setDeleteResultMessage(
          response?.data?.message || 'Record deleted successfully!'
        );
      } else {
        setDeleteResultVariant('danger');
        setDeleteResultMessage(
          response?.data?.message || 'Failed to delete record.'
        );
      }
    } catch (error) {
      const data = error?.response?.data;
      const msg =
        (typeof data?.message === 'string' && data.message) ||
        (typeof data?.error === 'string' && data.error) ||
        error?.message ||
        'Error deleting record. Please try again.';
      setDeleteResultVariant('danger');
      setDeleteResultMessage(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  const renderReadOnlyCheckbox = rawValue => {
    const checked =
      rawValue === true ||
      rawValue === 'true' ||
      rawValue === 1 ||
      rawValue === '1';

    return (
      <div className='detail-checkbox-wrapper' title={checked ? 'Yes' : 'No'}>
        <Form.Check
          type='checkbox'
          checked={checked}
          readOnly
          disabled
          className='detail-readonly-checkbox'
        />
      </div>
    );
  };

  /** Prefer object_config data_type over `type` (layout area is often stored as "body"). */
  const resolveDetailFieldType = (field) => {
    const layoutAreaTokens = new Set([
      'body',
      'detail',
      'hidden',
      'highlight_section',
    ]);
    const candidates = [
      field?.data_type,
      field?.dataType,
      field?.type,
      field?.value && typeof field.value === 'object' && !Array.isArray(field.value)
        ? field.value.type
        : null,
    ];
    for (const c of candidates) {
      if (c == null || String(c).trim() === '') continue;
      const t = String(c).trim().toLowerCase();
      if (!layoutAreaTokens.has(t)) return t;
    }
    return '';
  };

  const isRichTextDetailField = (field, rawValue, resolvedType) => {
    if (resolvedType === 'richtext') return true;
    const fn = String(field?.fieldName || field?.field_name || '').toLowerCase();
    if (!fn.endsWith('_html') && fn !== 'body') return false;
    const s = rawValue != null ? String(rawValue).trim() : '';
    return s.length > 0 && /<[a-z][\s\S]*>/i.test(s);
  };

  /** Detail view API uses camelCase; object-config payloads use snake_case — normalize once. */
  const normalizeDetailLayoutField = (raw) => {
    if (!raw || typeof raw !== 'object') return raw;
    const fieldName = raw.fieldName ?? raw.field_name ?? '';
    const label = raw.label ?? raw.field_label ?? fieldName;
    const dataType = raw.data_type ?? raw.dataType ?? null;
    const typeToken =
      raw.type != null && String(raw.type).trim() !== '' ? raw.type : dataType;
    return {
      ...raw,
      fieldName: String(fieldName),
      field_name: raw.field_name ?? fieldName,
      label,
      field_label: raw.field_label ?? label,
      data_type: dataType ?? raw.data_type,
      dataType: raw.dataType ?? dataType,
      type: typeToken ?? raw.type,
    };
  };

  // Helper function to safely extract primitive value from potentially nested objects
  const extractPrimitiveValue = (value) => {
    if (value === null || value === undefined) {
      return null;
    }

    // If it's already a primitive (not an object or array), return it
    if (typeof value !== 'object' || Array.isArray(value)) {
      return value;
    }

    // If it's an object, try to extract a primitive value
    // Priority: value > displayValue > convert to string
    if (value.value !== undefined) {
      // Recursively extract if value is also an object
      const extracted = extractPrimitiveValue(value.value);
      if (extracted !== null) return extracted;
    }

    if (value.displayValue !== undefined) {
      const extracted = extractPrimitiveValue(value.displayValue);
      if (extracted !== null) return extracted;
    }

    // If we can't extract a primitive, convert to string as last resort
    try {
      return String(value);
    } catch {
      return null;
    }
  };

  const isBlankDetailString = (value) => {
    if (value == null) return true;
    const trimmed = String(value).trim();
    if (trimmed === '' || trimmed === '—' || trimmed === '-') return true;
    if (/<[a-z][\s\S]*>/i.test(trimmed)) {
      // Rich text with only media (e.g. <img>) has no text after stripping tags —
      // still treat as non-blank so Hide Blank Fields keeps the field visible.
      if (/<(img|video|audio|iframe|object|embed|source)\b/i.test(trimmed)) {
        return false;
      }
      const textOnly = trimmed
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .trim();
      return textOnly === '';
    }
    return false;
  };

  /** Whether a detail/highlight field has no meaningful value (for "Hide blank fields"). */
  const isDetailFieldBlank = (field) => {
    const f = normalizeDetailLayoutField(field);
    const rawValue = extractPrimitiveValue(f.value);
    const rawDisplay =
      f.displayValue !== undefined ? extractPrimitiveValue(f.displayValue) : null;
    const fieldType = resolveDetailFieldType(f);

    if (fieldType === 'checkbox' || fieldType === 'boolean') {
      return false;
    }
    if (typeof rawValue === 'boolean') {
      return false;
    }
    if (typeof rawValue === 'number' && !Number.isNaN(rawValue)) {
      return false;
    }
    if (typeof rawDisplay === 'number' && !Number.isNaN(rawDisplay)) {
      return false;
    }
    if (fieldType === 'password' && rawValue != null && rawValue !== '') {
      return false;
    }
    if (isFieldValueMasked(f) && rawValue != null && rawValue !== '') {
      return false;
    }

    if (fieldType === 'calculated' || f.type === 'calculated') {
      const displayConfig = f.displayConfig ?? f.display_config;
      const normalized = normalizeCalculatedDisplayConfig(displayConfig);
      if (normalized.returnType === 'url') {
        return !pickDetailUrlSource(rawValue, rawDisplay);
      }
      return rawValue === null || rawValue === undefined || rawValue === '';
    }

    if (fieldType === 'lookup') {
      const idStr = rawValue != null ? String(rawValue).trim() : '';
      if (idStr === '') return true;
      // Soft-deleted / missing parent: no resolvable label → treat as empty
      const disp = rawDisplay != null ? String(rawDisplay).trim() : '';
      return disp === '';
    }

    if (Array.isArray(rawValue)) {
      return rawValue.length === 0;
    }

    const effective =
      rawDisplay != null && !isBlankDetailString(rawDisplay) ? rawDisplay : rawValue;

    if (effective == null) return true;
    if (Array.isArray(effective)) return effective.length === 0;

    return isBlankDetailString(effective);
  };

  const filterDetailLayoutFields = (fields, { taskType, mailConfigType } = {}) => {
    return (fields || []).filter(field => {
      if (
        objectName === 'task' &&
        field?.fieldName?.toLowerCase() === 'related_record_id'
      ) {
        return false;
      }
      if (objectName === 'task') {
        if (!isFieldVisible(field.fieldName, taskType)) return false;
      }
      if (objectName === 'mail_config') {
        if (!isMailConfigFieldVisible(field.fieldName, mailConfigType)) return false;
      }
      if (blankFieldsMode === 'hide_blank' && isDetailFieldBlank(field)) {
        return false;
      }
      if (blankFieldsMode === 'show_blank' && !isDetailFieldBlank(field)) {
        return false;
      }
      return true;
    });
  };

  const renderCalculatedField = (field) => {
    const displayConfig = field.displayConfig ?? field.display_config;
    const normalized = normalizeCalculatedDisplayConfig(displayConfig);
    const { merged: mergedConfig } = flattenDisplayConfig(displayConfig);

    // Extract value if field.value is an object (handle nested field objects)
    let value = extractPrimitiveValue(field.value);
    const valueFromDisplay =
      field.displayValue !== undefined ? extractPrimitiveValue(field.displayValue) : null;
    const calculatedUrlSrc =
      normalized.returnType === 'url' ? pickDetailUrlSource(value, valueFromDisplay) : '';

    if (normalized.returnType !== 'url' && (value === null || value === undefined || value === '')) {
      return '—';
    }
    if (normalized.returnType === 'url' && !calculatedUrlSrc) {
      return '—';
    }

    const resolveNumber = (key, fallback) => {
      const numeric = toFiniteNumber(mergedConfig[key]);
      return numeric !== null ? numeric : fallback;
    };

    const resolveBoolean = (key, fallback) => {
      if (mergedConfig[key] === true || mergedConfig[key] === 'true' || mergedConfig[key] === 1 || mergedConfig[key] === '1') {
        return true;
      }
      if (mergedConfig[key] === false || mergedConfig[key] === 'false' || mergedConfig[key] === 0 || mergedConfig[key] === '0') {
        return false;
      }
      return fallback;
    };

    switch (normalized.returnType) {
      case 'currency':
        return formatCalculatedFieldForDisplay(
          { value, displayConfig },
          { currency: getRecordFieldValue('currency') }
        );
      case 'number':
      case 'percent':
      case 'boolean':
      case 'text':
        return formatCalculatedFieldForDisplay({ value, displayConfig });
      case 'checkbox':
        return renderReadOnlyCheckbox(value);

      case 'date':
        return formatDate(value);

      case 'datetime':
        return formatDateTime(value);

      case 'time':
        return formatTime(value);

      case 'url': {
        const href = normalizeDetailUrlHref(calculatedUrlSrc);
        const displayText =
          formatCalculatedValue(value, displayConfig) ||
          pickDetailUrlSource(valueFromDisplay, value) ||
          calculatedUrlSrc;
        return (
          <a
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            className='link-brand'
            title='Opens in a new tab'
            style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontWeight: theme.typography.fontWeight.medium,
              textDecoration: 'none',
              transition: theme.transitions.normal,
              display: 'inline-block',
              maxWidth: '100%',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {displayText}
          </a>
        );
      }

      case 'image': {
        const isBrowser = typeof window !== 'undefined';
        const isMobile = isBrowser ? window.innerWidth <= 768 : false;
        const resolveDimension = (primaryKey, secondaryKey, fallback) => {
          const primary = toFiniteNumber(mergedConfig[primaryKey]);
          if (primary !== null) {
            return primary;
          }
          if (secondaryKey) {
            const secondary = toFiniteNumber(mergedConfig[secondaryKey]);
            if (secondary !== null) {
              return secondary;
            }
          }
          return fallback;
        };

        const width = isMobile
          ? resolveDimension('mobileWidth', 'width', 150)
          : resolveDimension('width', null, 300);
        const height = isMobile
          ? resolveDimension('mobileHeight', 'height', 150)
          : resolveDimension('height', null, 300);

        const allowedFits = ['contain', 'cover', 'fill', 'scale-down', 'none'];
        const fitRaw = typeof mergedConfig.objectFit === 'string' ? mergedConfig.objectFit.toLowerCase() : '';
        const objectFit = allowedFits.includes(fitRaw) ? fitRaw : 'contain';

        return (
          <img
            src={resolveImageSrc(value)}
            alt={field.label}
            style={{
              width: width !== null ? `${width}px` : 'auto',
              height: height !== null ? `${height}px` : 'auto',
              maxWidth: '100%',
              objectFit,
              borderRadius: '4px',
              border: '1px solid #e0e0e0'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = '<span style="color: #999;">Image not available</span>';
            }}
          />
        );
      }

      case 'barcode': {
        const format = typeof mergedConfig.format === 'string' && mergedConfig.format.trim() !== ''
          ? mergedConfig.format.trim().toUpperCase()
          : 'CODE128';
        const width = resolveNumber('width', 2);
        const height = resolveNumber('height', 100);
        const displayValue = resolveBoolean('displayValue', true);

        return (
          <Barcode
            value={String(value)}
            format={format}
            width={width ?? 2}
            height={height ?? 100}
            displayValue={displayValue}
          />
        );
      }

      case 'qrcode': {
        const size = resolveNumber('size', 200);
        const levelRaw = typeof mergedConfig.errorCorrectionLevel === 'string'
          ? mergedConfig.errorCorrectionLevel.toUpperCase()
          : '';
        const validLevels = ['L', 'M', 'Q', 'H'];
        const level = validLevels.includes(levelRaw) ? levelRaw : 'M';

        return (
          <QRCode
            value={String(value)}
            size={size ?? 200}
            level={level}
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        );
      }

      default:
        return formatCalculatedValue(value, displayConfig);
    }
  };

  /** Lazy-load one related list tab; results are cached per (objectName, id, tabId). Pass `{ force: true }` to refetch. */
  const fetchSingleRelatedTab = useCallback(
    async (tabId, options = {}) => {
      const force = options.force === true;
      if (!objectName || !id || tabId == null || tabId === '') {
        return;
      }
      const tid = String(tabId);
      const cacheKey = `${objectName}::${id}::${tid}`;
      if (force) {
        loadedRelatedTabCacheRef.current.delete(cacheKey);
      }
      if (!force && loadedRelatedTabCacheRef.current.has(cacheKey)) {
        return;
      }
      if (inFlightRelatedTabsRef.current.has(tid)) {
        return;
      }
      inFlightRelatedTabsRef.current.add(tid);
      setLazyRelatedTabLoading((prev) => ({ ...prev, [tid]: true }));
      try {
        const dataApi = new DataApi(`relatedlist/records/${objectName}/${id}/tab`);
        const response = await dataApi.fetchById(tid);
        const payload = response?.data?.data;
        const records = Array.isArray(payload?.records) ? payload.records : [];
        setLazyRelatedTabRecords((prev) => ({ ...prev, [tid]: records }));
        loadedRelatedTabCacheRef.current.add(cacheKey);
      } catch (e) {
        console.error('Failed to load related tab records:', e);
        setLazyRelatedTabRecords((prev) => ({ ...prev, [tid]: [] }));
        loadedRelatedTabCacheRef.current.add(cacheKey);
      } finally {
        inFlightRelatedTabsRef.current.delete(tid);
        setLazyRelatedTabLoading((prev) => ({ ...prev, [tid]: false }));
      }
    },
    [objectName, id]
  );

  const resolveRelatedListTabFetchId = useCallback((tab) => {
    if (!tab) return null;
    const raw = tab.tabId ?? tab.config?.related_list_id;
    if (raw == null || raw === '') return null;
    return String(raw);
  }, []);

  const switchTab = useCallback(
    (tabName) => {
      setActiveTab(tabName);
      if (
        typeof tabName === 'string' &&
        (tabName.startsWith('layoutEmbed:') || tabName.startsWith('custom:'))
      ) {
        setLazyDetailTabContentKeys((prev) => {
          if (prev.has(tabName)) return prev;
          const next = new Set(prev);
          next.add(tabName);
          return next;
        });
      }
      const tabsMeta = recordDataRef.current?.tabs;
      if (!Array.isArray(tabsMeta)) {
        return;
      }
      const meta = tabsMeta.find((t) => t.tabName === tabName);
      const tid = resolveRelatedListTabFetchId(meta);
      if (!tid) {
        return;
      }
      fetchSingleRelatedTab(tid);
    },
    [fetchSingleRelatedTab, resolveRelatedListTabFetchId]
  );

  /** Related list toolbar refresh: one tab API only; does not reload the detail record. */
  const handleRelatedListTabRefresh = useCallback(
    (tab) => {
      const tid = resolveRelatedListTabFetchId(tab);
      if (!tid) return;
      fetchSingleRelatedTab(tid, { force: true });
    },
    [fetchSingleRelatedTab, resolveRelatedListTabFetchId]
  );

  /** After add/update/delete in embedded related grid: refetch active tab rows only (fast). */
  const refreshActiveRelatedTabRowsOnly = useCallback(() => {
    const at = activeTabRef.current;
    if (typeof at === 'string' && at.startsWith('__searchWidget_')) {
      return;
    }
    const tabsMeta = recordDataRef.current?.tabs;
    if (!Array.isArray(tabsMeta)) return;
    const meta = tabsMeta.find((t) => t.tabName === at);
    const tid = meta?.tabId ?? meta?.config?.related_list_id;
    if (tid != null && tid !== '') {
      fetchSingleRelatedTab(String(tid), { force: true });
    }
  }, [fetchSingleRelatedTab]);

  // Function to refresh the record data (including related lists)
  const refreshRecordData = async () => {
    console.log('$$ refreshRecordData');
    try {
      const dataApi = new DataApi(`object/view/${objectName}`);
      const response = await dataApi.fetchById(id);
      const data = response?.data?.data;

      if (!data || Object.keys(data).length === 0) {
        setError('No Record Found!');
      } else {
        setError(null);
        loadedRelatedTabCacheRef.current.clear();
        inFlightRelatedTabsRef.current.clear();
        setLazyRelatedTabRecords({});
        setLazyRelatedTabLoading({});
        setRecordData(data);
        console.log('Record data refreshed:', data);
        console.log('Related tabs data refreshed:', data?.tabs);
        const at = activeTabRef.current;
        const meta = Array.isArray(data.tabs) ? data.tabs.find((t) => t.tabName === at) : null;
        const tid = meta?.tabId ?? meta?.config?.related_list_id;
        if (tid != null && tid !== '') {
          fetchSingleRelatedTab(String(tid));
        }
      }
    } catch (error) {
      console.error('Failed to refresh record:', error);
      setError(error?.userMessage ?? getApiErrorMessage(error));
    }
  };

  const scheduleAsyncWorkflowFollowupRefresh = () => {
    if (asyncWorkflowFollowupRefreshTimerRef.current != null) {
      clearTimeout(asyncWorkflowFollowupRefreshTimerRef.current);
    }
    asyncWorkflowFollowupRefreshTimerRef.current = setTimeout(() => {
      asyncWorkflowFollowupRefreshTimerRef.current = null;
      refreshRecordData();
    }, 2000);
  };

  /** After PUT/create the server runs onUpdate workflows in setImmediate; detail was only refetching pre-workflow state. */
  const refreshDetailAfterSave = () => {
    refreshRecordData();
    scheduleAsyncWorkflowFollowupRefresh();
  };

  useEffect(() => {
    return () => {
      if (asyncWorkflowFollowupRefreshTimerRef.current != null) {
        clearTimeout(asyncWorkflowFollowupRefreshTimerRef.current);
        asyncWorkflowFollowupRefreshTimerRef.current = null;
      }
    };
  }, [objectName, id]);

  const closeWebComponentModal = useCallback(() => {
    setWebComponentModal({
      show: false,
      loading: false,
      submitting: false,
      formConfig: null,
      loadError: null,
      submitFeedbackBanner: null,
      formKey: 0,
      modalTitle: '',
      result: null,
      resolvedViewPageProps: null,
    });
  }, []);

  const openWebComponentModalForAction = useCallback(
    async (action) => {
      let cfg = action?.config;
      if (typeof cfg === 'string') {
        try {
          cfg = JSON.parse(cfg);
        } catch {
          cfg = null;
        }
      }
      const rawId =
        cfg &&
          (cfg.viewPageScriptId ??
            cfg.view_page_script_id ??
            cfg.webComponentScriptId ??
            cfg.web_component_script_id ??
            cfg.uiSchemaScriptId ??
            cfg.ui_schema_script_id) != null
          ? String(
            cfg.viewPageScriptId ??
            cfg.view_page_script_id ??
            cfg.webComponentScriptId ??
            cfg.web_component_script_id ??
            cfg.uiSchemaScriptId ??
            cfg.ui_schema_script_id
          ).trim()
          : '';
      const rawKey =
        cfg &&
          (cfg.viewPageScriptKey ??
            cfg.view_page_script_key ??
            cfg.webComponentScriptKey ??
            cfg.web_component_script_key ??
            cfg.uiSchemaScriptKey ??
            cfg.ui_schema_script_key) != null
          ? String(
            cfg.viewPageScriptKey ??
            cfg.view_page_script_key ??
            cfg.webComponentScriptKey ??
            cfg.web_component_script_key ??
            cfg.uiSchemaScriptKey ??
            cfg.ui_schema_script_key
          ).trim()
          : '';
      const identifier =
        (rawId && ACTION_CONFIG_UUID_RE.test(rawId) ? rawId : '') || rawKey;
      if (!identifier) {
        setToast({
          show: true,
          message:
            'This view page action is missing config (script id/key fields).',
          variant: 'warning',
        });
        return;
      }
      const expectedScriptKey =
        cfg && cfg.scriptKey != null ? String(cfg.scriptKey).trim() : '';
      const aid = action?.action_id != null ? String(action.action_id) : null;
      if (aid) setLoadingWebComponentActionId(aid);
      const resolvedViewPageProps = buildDetailCustomActionProps(action, {
        layoutObjectName: objectName,
        getFieldValueForMapping: getRelationshipFilterValue,
      });
      if (typeof console !== 'undefined' && console.info) {
        console.info('[ibs-spark:viewPageCustomScript] open modal', {
          recordId: id,
          objectName,
          actionLabel: action?.label || action?.action_name,
          propMappings: cfg?.propMappings ?? null,
          resolvedViewPageProps,
        });
      }
      setWebComponentModal((s) => ({
        ...s,
        show: true,
        loading: true,
        submitting: false,
        formConfig: null,
        loadError: null,
        submitFeedbackBanner: null,
        modalTitle: action?.label || action?.action_name || 'Form',
        formKey: s.formKey + 1,
        resolvedViewPageProps,
      }));
      try {
        const res = await getCustomScriptByIdOrKey(identifier);
        if (!res?.success) {
          throw new Error(res?.message || 'Failed to load view page script');
        }
        {
          const st = String(res.data?.script_type || '').toLowerCase();
          const uiFormTypes = new Set(['view_page', 'web_component', 'ui_schema']);
          if (!uiFormTypes.has(st)) {
            throw new Error(
              'Linked script is not a view page form (script_type must be view_page).'
            );
          }
        }
        if (
          expectedScriptKey &&
          res.data?.script_key &&
          String(res.data.script_key).trim() !== expectedScriptKey
        ) {
          throw new Error('Script key does not match action configuration.');
        }
        const code = res.data?.code;
        if (!code || String(code.type).toLowerCase() !== 'form') {
          throw new Error('Invalid view page form: expected config.code.type "form".');
        }
        let nextModalTitle = null;
        if (code && typeof code === 'object') {
          if (Object.prototype.hasOwnProperty.call(code, 'modalTitle')) {
            nextModalTitle =
              code.modalTitle == null ? '' : String(code.modalTitle).trim();
          } else if (Object.prototype.hasOwnProperty.call(code, 'modal_title')) {
            nextModalTitle =
              code.modal_title == null ? '' : String(code.modal_title).trim();
          }
        }
        setWebComponentModal((s) => ({
          ...s,
          loading: false,
          formConfig: code,
          ...(nextModalTitle !== null ? { modalTitle: nextModalTitle } : {}),
        }));
      } catch (e) {
        const msg =
          e?.response?.data?.message || e?.message || 'Failed to load form definition';
        setWebComponentModal((s) => ({
          ...s,
          loading: false,
          loadError: msg,
        }));
      } finally {
        setLoadingWebComponentActionId(null);
      }
    },
    [objectName, getRelationshipFilterValue]
  );

  const handleWebComponentModalSubmit = useCallback(
    async (formData) => {
      const cfg = webComponentModal.formConfig;
      const feedbackInline = resolveViewPageSubmitFeedbackMode(cfg) === 'inline';
      const submitToastExtras = resolveViewPageSubmitToastExtras(cfg);
      const actionScriptId = cfg?.submit?.actionScriptId;
      if (!actionScriptId) {
        const msg = 'Form config is missing submit.actionScriptId.';
        if (feedbackInline) {
          setWebComponentModal((s) => ({
            ...s,
            submitFeedbackBanner: { variant: 'warning', message: msg },
          }));
        } else {
          setToast({
            show: true,
            message: msg,
            variant: 'warning',
            ...submitToastExtras,
          });
        }
        return;
      }
      setWebComponentModal((s) => ({
        ...s,
        submitting: true,
        submitFeedbackBanner: null,
      }));
      try {
        const baseProps =
          webComponentModal.resolvedViewPageProps &&
            typeof webComponentModal.resolvedViewPageProps === 'object' &&
            !Array.isArray(webComponentModal.resolvedViewPageProps)
            ? webComponentModal.resolvedViewPageProps
            : {};
        const inputData = { ...baseProps, ...formData };
        if (typeof console !== 'undefined' && console.info) {
          console.info('[ibs-spark:viewPageCustomScript] submit → /api/custom-scripts/run', {
            recordId: id,
            objectName,
            actionScriptId: String(actionScriptId).trim(),
            mergedFromProps: baseProps,
            formValues: formData,
            inputData,
          });
        }
        const runParams = { recordId: id, objectName, inputData };
        if (ACTION_CONFIG_UUID_RE.test(String(actionScriptId).trim())) {
          runParams.customScriptId = String(actionScriptId).trim();
        } else {
          runParams.scriptKey = String(actionScriptId).trim();
        }
        const res = await runCustomScriptPayload(runParams);
        if (!res?.success) {
          const msg = res?.message || 'Request failed';
          if (feedbackInline) {
            setWebComponentModal((s) => ({
              ...s,
              submitting: false,
              submitFeedbackBanner: { variant: 'danger', message: msg },
            }));
          } else {
            setToast({
              show: true,
              message: msg,
              variant: 'danger',
              ...submitToastExtras,
            });
            setWebComponentModal((s) => ({ ...s, submitting: false }));
          }
          return;
        }
        const vm = res.data;
        if (vm && vm.success === false) {
          const msg = vm.message || 'Validation failed';
          if (feedbackInline) {
            setWebComponentModal((s) => ({
              ...s,
              submitting: false,
              submitFeedbackBanner: { variant: 'danger', message: msg },
              ...(vm.mappingDebug != null
                ? { result: { mappingDebug: vm.mappingDebug, message: vm.message } }
                : {}),
            }));
          } else {
            setToast({
              show: true,
              message: msg,
              variant: 'danger',
              ...submitToastExtras,
            });
            setWebComponentModal((s) => ({
              ...s,
              submitting: false,
              ...(vm.mappingDebug != null
                ? { result: { mappingDebug: vm.mappingDebug, message: vm.message } }
                : {}),
            }));
          }
          if (vm.mappingDebug != null) {
            if (typeof console !== 'undefined' && console.warn) {
              console.warn('[ibs-spark:convertToContact] mappingDebug (failed)', vm.mappingDebug);
            }
          }
          return;
        }
        if (vm && vm.mappingDebug != null) {
          if (typeof console !== 'undefined' && console.info) {
            console.info('[ibs-spark:convertToContact] mappingDebug', vm.mappingDebug);
          }
          const md = vm.mappingDebug;
          const missing =
            (Array.isArray(md.uniqMissingOpp) && md.uniqMissingOpp.length > 0) ||
            (Array.isArray(md.uniqMissingLead) && md.uniqMissingLead.length > 0);
          const emptySrc =
            md.hasEmptySourceValues === true ||
            (Array.isArray(md.skippedOpp) && md.skippedOpp.length > 0) ||
            (Array.isArray(md.skippedLead) && md.skippedLead.length > 0);
          const dbgMsg = missing
            ? 'Mapping debug: source row is missing configured fields. See Result below.'
            : emptySrc
              ? 'Mapping debug: some source values were empty (skipped). See Result below.'
              : 'Mapping debug — see Result below.';
          const dbgVariant = missing ? 'warning' : emptySrc ? 'info' : 'success';
          if (feedbackInline) {
            setWebComponentModal((s) => ({
              ...s,
              submitting: false,
              submitFeedbackBanner: { variant: dbgVariant, message: dbgMsg },
            }));
          } else {
            setToast({
              show: true,
              message: dbgMsg,
              variant: dbgVariant,
              ...submitToastExtras,
              ...(!submitToastExtras.sticky ? { delay: submitToastExtras.delay ?? 12000 } : {}),
            });
          }
          if (vm.refreshRecord === true) {
            await refreshRecordData();
          }
          setWebComponentModal((s) => ({
            ...s,
            submitting: false,
            result: vm.mappingDebug,
          }));
          return;
        }
        const redirectUrl = resolveCustomScriptRedirectUrl(vm);
        if (redirectUrl) {
          stashPendingRedirectToast({
            message: res.message || vm?.message || 'Done',
            variant: normalizeCustomScriptToastVariant(vm),
          });
          closeWebComponentModal();
          window.location.assign(redirectUrl);
          return;
        }
        const vmKeepOpen = !!(vm && (vm.keepOpen === true || vm.resultView));
        const doneMsg = res.message || vm?.message || 'Done';
        if (feedbackInline && vmKeepOpen) {
          setWebComponentModal((s) => ({
            ...s,
            submitting: false,
            submitFeedbackBanner: { variant: 'success', message: doneMsg },
          }));
        } else {
          setToast({
            show: true,
            message: doneMsg,
            variant: 'success',
            delay: undefined,
            ...submitToastExtras,
          });
        }
        if (vm && vm.refreshRecord === true) {
          await refreshRecordData();
        }
        if (vmKeepOpen) {
          setWebComponentModal((s) => ({
            ...s,
            submitting: false,
            result: vm.resultView != null ? vm.resultView : vm,
          }));
        } else {
          closeWebComponentModal();
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Submit failed';
        if (feedbackInline) {
          setWebComponentModal((s) => ({
            ...s,
            submitFeedbackBanner: { variant: 'danger', message: msg },
          }));
        } else {
          setToast({
            show: true,
            message: msg,
            variant: 'danger',
            ...submitToastExtras,
          });
        }
      } finally {
        setWebComponentModal((s) => ({ ...s, submitting: false }));
      }
    },
    [
      webComponentModal.formConfig,
      webComponentModal.resolvedViewPageProps,
      id,
      objectName,
      closeWebComponentModal,
      refreshRecordData,
    ]
  );

  // Handler for path stage changes
  const handlePathStageChange = async (fieldName, newValue) => {
    // Prevent multiple simultaneous updates
    if (updatingPathField) {
      console.log('Update already in progress, ignoring...');
      return;
    }

    try {
      setUpdatingPathField(true);
      console.log(`🔄 Updating ${fieldName} to ${newValue}`);
      console.log(`📍 Object: ${objectName}, Record ID: ${id}`);

      const updateData = { [fieldName]: newValue };
      const token = localStorage.getItem('token');
      const url = `${constants.API_BASE_URL}/api/object/${objectName}/${id}`;

      console.log(`📤 Calling PUT ${url}`);
      console.log(`📦 Payload:`, updateData);

      // Using axios directly for more control and clarity
      const response = await axios.put(url, updateData, {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
      });

      console.log(`📥 Response:`, response);

      if (response?.data?.success || response?.status === 200) {
        console.log(`✅ Successfully updated ${fieldName} to ${newValue}`);

        setToast({
          show: true,
          message: 'Stage updated successfully!',
          variant: 'success',
        });

        // Refresh the record data to show updated stage (incl. delayed refetch for async workflows)
        await refreshRecordData();
        scheduleAsyncWorkflowFollowupRefresh();
      } else {
        throw new Error(response?.data?.message || 'Failed to update stage');
      }
    } catch (error) {
      console.error('❌ Error updating path stage:', error);
      console.error('❌ Error details:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url
      });

      const data = getApiErrorResponseData(error);
      const errorMessage =
        data?.message ||
        data?.error ||
        error?.response?.message ||
        error?.message ||
        'Failed to update stage';

      const hints = parseApiToastHints(data?.toast);
      setToast({
        show: true,
        message: errorMessage,
        variant: 'danger',
        ...(hints
          ? {
            sticky: hints.sticky,
            delay: hints.delay,
            ...(hints.messageFontSize
              ? { messageFontSize: hints.messageFontSize }
              : {}),
          }
          : {}),
      });
    } finally {
      setUpdatingPathField(false);
    }
  };

  // Modal handlers
  const handleEditModalClose = () => {
    setShowEditModal(false);
  };

  const handleEditModalSave = savedData => {
    console.log('Record saved from edit modal:', savedData);
    setShowEditModal(false);

    setToast({
      show: true,
      message: 'Record updated successfully!',
      variant: 'success',
    });

    // Refresh the record data to show updated information (incl. delayed refetch for async workflows)
    refreshDetailAfterSave();
  };

  const handleEditModalCancel = () => {
    setShowEditModal(false);
  };

  // Clone Modal handlers
  const handleCloneModalClose = () => {
    setShowCloneModal(false);
  };

  const handleCloneModalSave = savedData => {
    console.log('Record cloned successfully:', savedData);
    setShowCloneModal(false);

    const newRecordId = savedData?.id || savedData?.data?.id;
    if (newRecordId) {
      stashPendingRedirectToast({
        message: 'Record cloned successfully!',
        variant: 'success',
      });
      window.location.href = `/view/${objectName}/${newRecordId}`;
    } else {
      setToast({
        show: true,
        message: 'Record cloned successfully!',
        variant: 'success',
      });
    }
  };

  const handleCloneModalCancel = () => {
    setShowCloneModal(false);
  };

  const handleNewRecordModalClose = () => {
    setShowNewRecordModal(false);
  };

  const handleNewRecordModalSave = savedData => {
    setShowNewRecordModal(false);
    setToast({
      show: true,
      message: 'Record created successfully!',
      variant: 'success',
    });
    const newRecordId = savedData?.id || savedData?.data?.id;
    if (newRecordId) {
      window.location.href = `/view/${objectName}/${newRecordId}`;
    }
  };

  const handleNewRecordModalCancel = () => {
    setShowNewRecordModal(false);
  };

  useEffect(() => {
    let cancelled = false;

    if (!objectName || id == null || String(id).trim() === '') {
      setRecordData(null);
      setError('Invalid record link.');
      setIsLoaded(true);
      return undefined;
    }

    setIsLoaded(false);
    setError(null);
    setRecordData(null);

    const fetchRecord = async () => {
      try {
        const dataApi = new DataApi(`object/view/${objectName}`);
        const response = await dataApi.fetchById(id);
        if (cancelled) return;
        const data = response?.data?.data;

        if (!data || Object.keys(data).length === 0) {
          setError('No Record Found!');
        } else {
          setRecordData(data);
          console.log('Record data loaded:', data);
          console.log('Related tabs data:', data?.tabs);
          console.log('Widgets data:', data?.widgets);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch record:', error);
        setError(error?.userMessage ?? getApiErrorMessage(error));
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    };

    fetchRecord();
    return () => {
      cancelled = true;
    };
  }, [objectName, id]);

  useEffect(() => {
    loadedRelatedTabCacheRef.current.clear();
    inFlightRelatedTabsRef.current.clear();
    setLazyRelatedTabRecords({});
    setLazyRelatedTabLoading({});
  }, [objectName, id]);

  useEffect(() => {
    setLazyDetailTabContentKeys(new Set());
  }, [objectName, id]);

  useEffect(() => {
    setLayoutWidgetsMountReady(false);
  }, [objectName, id]);

  useEffect(() => {
    if (!isLoaded || !recordData) return;

    const routeId = id != null && id !== '' ? String(id) : '';
    const dataRecordId =
      recordData?.recordId != null
        ? String(recordData.recordId)
        : recordData?.id != null
          ? String(recordData.id)
          : '';

    if (routeId && dataRecordId && dataRecordId !== routeId) {
      return;
    }

    let cancelled = false;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) setLayoutWidgetsMountReady(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isLoaded, objectName, id, recordData]);

  useEffect(() => {
    if (!objectName) {
      setObjectConfig(null);
      return;
    }

    let isMounted = true;

    const fetchObjectConfig = async () => {
      try {
        const configApi = new DataApi(`objectconfig/${objectName}`);
        const response = await configApi.fetchAll();
        if (!isMounted) return;

        if (response?.data?.success) {
          setObjectConfig(response.data.data);
        } else {
          setObjectConfig(null);
        }
      } catch (error) {
        console.error('Error fetching object config:', error);
        if (isMounted) {
          setObjectConfig(null);
        }
      }
    };

    fetchObjectConfig();

    return () => {
      isMounted = false;
    };
  }, [objectName]);

  useEffect(() => {
    const on = objectName != null ? String(objectName).trim() : '';
    const cfgName =
      objectConfig?.object_name != null ? String(objectConfig.object_name).trim().toLowerCase() : '';
    if (
      !objectConfig?.id ||
      !on ||
      on.toLowerCase().endsWith('_history') ||
      cfgName !== on.toLowerCase()
    ) {
      setFieldHistoryTrackedFieldNames(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await ObjectConfigApi.getFieldHistoryMapping(objectConfig.id);
        if (cancelled) {
          return;
        }
        if (res.success && Array.isArray(res.data)) {
          setFieldHistoryTrackedFieldNames(
            new Set(res.data.map(row => row.field_name).filter(Boolean))
          );
        } else {
          setFieldHistoryTrackedFieldNames(new Set());
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('Field history mapping fetch failed (path visit styling disabled):', e);
          setFieldHistoryTrackedFieldNames(new Set());
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [objectName, objectConfig?.id, objectConfig?.object_name]);


  // Listen for incoming WhatsApp messages and auto-open chat
  useEffect(() => {
    if (!socket || !recordData) return;

    const phoneNumber = recordData?.details
      ?.flatMap(s => s.fields)
      ?.find(f => f.fieldName === 'phone')?.value;

    if (!phoneNumber) return;

    const handleIncomingMessage = (data) => {
      // Extract phone number from socket data
      const socketNumber = (
        data?.recipient_id ||
        data?.to ||
        data?.from ||
        ''
      ).replace(/\D/g, '');

      // Extract phone number from current record
      const currentNumber = phoneNumber?.toString().replace(/\D/g, '');

      // Open chat if the incoming message is for this record's phone number
      if (socketNumber === currentNumber) {
        setShowWhatsAppChat(true);
      }
    };

    socket.on('receivedwhatsappmessage', handleIncomingMessage);

    return () => {
      socket.off('receivedwhatsappmessage', handleIncomingMessage);
    };
  }, [socket, recordData]);

  // WhatsApp chat should only open when button is clicked, not automatically

  // Check for success message in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const successMessage = urlParams.get('success');

    if (successMessage) {
      console.log('Success message found in URL:', successMessage);
      setToast({
        show: true,
        message: decodeURIComponent(successMessage),
        variant: 'success',
      });

      // Remove success parameter from URL without page reload
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.delete('success');
      window.history.replaceState({}, '', currentUrl.toString());
    }
  }, []);

  // Calculate dynamic max visible tabs based on container width
  useEffect(() => {
    const calculateMaxVisibleTabs = () => {
      if (!tabsContainerRef.current || !tabsMeasureRef.current) return;

      const containerWidth = tabsContainerRef.current.getBoundingClientRect().width;
      if (!containerWidth || containerWidth <= 0) return;

      const measureEl = tabsMeasureRef.current;
      const gapRaw = window.getComputedStyle(measureEl).gap || '8px';
      const gap = Number.parseFloat(gapRaw) || 8;

      // Collect measured widths for all tabs (offscreen measurement buttons).
      const widths = [];
      tabMeasureButtonRefs.current.forEach((el) => {
        if (el) widths.push(el.getBoundingClientRect().width);
      });

      if (widths.length === 0) return;

      const moreWidth = tabsMoreMeasureRef.current
        ? tabsMoreMeasureRef.current.getBoundingClientRect().width
        : 100;

      const totalNoMore =
        widths.reduce((sum, w) => sum + w, 0) + gap * Math.max(0, widths.length - 1);

      // If everything fits, show all tabs (no dropdown).
      if (totalNoMore <= containerWidth) {
        if (maxVisibleTabs !== widths.length) {
          setMaxVisibleTabs(widths.length);
        }
        return;
      }

      // Otherwise, reserve space for "More" and fit as many leading tabs as possible.
      const reserve = moreWidth + gap; // "More" button plus one gap from last visible tab
      const available = Math.max(0, containerWidth - reserve);

      let fitCount = 0;
      let used = 0;
      for (let i = 0; i < widths.length; i++) {
        const next = widths[i] + (i === 0 ? 0 : gap);
        if (used + next <= available || i === 0) {
          used += next;
          fitCount++;
        } else {
          break;
        }
      }

      const minTabs = Math.min(3, widths.length);
      const finalCount = Math.max(minTabs, fitCount);

      if (maxVisibleTabs !== finalCount) {
        setMaxVisibleTabs(finalCount);
      }
    };

    // Calculate after a short delay to ensure DOM is rendered
    const timeoutId = setTimeout(() => {
      calculateMaxVisibleTabs();
    }, 150);

    // Recalculate on window resize with debounce
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        calculateMaxVisibleTabs();
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    // Use ResizeObserver for more accurate measurements
    let resizeObserver;
    if (tabsContainerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          calculateMaxVisibleTabs();
        }, 150);
      });
      resizeObserver.observe(tabsContainerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [
    // Recompute when the tab set changes or when record loads.
    recordData,
    maxVisibleTabs,
    normalizedCustomTabs?.length,
    layoutDetailEmbedTabsForDisplay?.length,
    detailTabsMergedForDisplay?.length
  ]);

  const renderDetailItem = field => {
    field = normalizeDetailLayoutField(field);
    // Extract value if field.value is an object (handle nested field objects)
    let rawValue = extractPrimitiveValue(field.value);
    const resolvedFieldType = resolveDetailFieldType(field);
    const isRichTextField = isRichTextDetailField(field, rawValue, resolvedFieldType);

    // Also extract displayValue if it exists and is an object
    let rawDisplayValue = field.displayValue !== undefined
      ? extractPrimitiveValue(field.displayValue)
      : null;

    let displayValue = rawValue ?? '—';

    // TeleCMI: recording player field (preferred) + filename display field
    if (
      objectName === 'telecmi_log' &&
      (field.fieldName === 'recording' || field.fieldName === 'file_name')
    ) {
      const filename =
        typeof rawValue === 'string' && rawValue.trim() !== ''
          ? rawValue.trim()
          : getRecordFieldValue('file_name');

      const isAudio =
        typeof filename === 'string' && /\.(mp3|wav|m4a)$/i.test(filename);

      if (field.fieldName === 'recording') {
        displayValue = isAudio ? (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            role="group"
            aria-label="Recording actions"
          >
            <button
              type="button"
              title="Play recording"
              aria-label="Play recording"
              onClick={() => setTelecmiPlayer({ show: true, filename })}
              style={{
                border: 'none',
                background: '#e9ecef',
                padding: 8,
                borderRadius: 8,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#212529',
              }}
            >
              <FaPlay size={14} />
            </button>
            <button
              type="button"
              title="Download recording"
              aria-label="Download recording"
              onClick={() => downloadTelecmiRecordingFile(filename)}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 6,
                borderRadius: 6,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.textSecondary || '#495057',
              }}
            >
              <FaDownload size={14} />
            </button>
          </div>
        ) : (
          '—'
        );
      } else if (field.fieldName === 'file_name') {
        // file_name is now display-only (no player)
        displayValue = filename || '—';
      }
    }

    // Special handling for password / masked fields - always show masked
    if ((field.type === 'password' || isFieldValueMasked(field)) && rawValue) {
      displayValue = (
        <span
          style={{
            fontFamily: 'monospace',
            letterSpacing: '2px',
          }}
        >
          {MASKED_FIELD_DISPLAY}
        </span>
      );
    }
    // Special handling for related_object field - use objectLabel if available
    else if (field.fieldName === 'related_object' && field.objectLabel) {
      displayValue = field.objectLabel;
    } else if (field.type === 'url') {
      const urlSrc = pickDetailUrlSource(rawValue, rawDisplayValue);
      if (!urlSrc) {
        displayValue = '—';
      } else {
        const href = normalizeDetailUrlHref(urlSrc);
        const labelText = pickDetailUrlSource(rawDisplayValue, rawValue) || urlSrc;
        displayValue = (
          <a
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            className='link-brand'
            title='Opens in a new tab'
            style={{
              fontFamily: theme.typography.fontFamily.primary,
              fontWeight: theme.typography.fontWeight.medium,
              textDecoration: 'none',
              transition: theme.transitions.normal,
              display: 'inline-block',
              maxWidth: '100%',
              overflowWrap: 'anywhere',
              wordBreak: 'break-all',
            }}
          >
            {labelText}
          </a>
        );
      }
    } else if (field.type === 'lookup') {
      // For lookup fields, use rawValue for the ID and displayValue for the text
      const lookupId = rawValue;
      const lookupIdStr = lookupId != null ? String(lookupId).trim() : '';
      const hasLookupDisplay =
        rawDisplayValue != null && String(rawDisplayValue).trim() !== '';
      // Soft-deleted / recycle-bin parents resolve with no display label — do not link
      if (lookupIdStr && !hasLookupDisplay) {
        displayValue = '—';
      } else {
        const lookupDisplay = rawDisplayValue || rawValue || '—';
        const refObj = field.referenceObject != null ? String(field.referenceObject).trim() : '';
        if (isRecordviewObjectLookupField(field)) {
          displayValue = (
            <span>
              {lookupDisplay}
            </span>
          );
        } else {
          const viewHref =
            lookupIdStr && refObj
              ? `/view/${encodeURIComponent(refObj)}/${encodeURIComponent(lookupIdStr)}`
              : '#';
          displayValue =
            lookupIdStr && refObj ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  maxWidth: '100%',
                  flexWrap: 'wrap',
                }}
              >
                <a
                  href={viewHref}
                  className='link-brand'
                  style={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontWeight: theme.typography.fontWeight.medium,
                    textDecoration: 'none',
                    transition: theme.transitions.normal,
                    cursor: 'pointer',
                  }}
                  title={openParentInModal ? 'Click to preview in a popup. Ctrl+click or ⌘+click to open in a new tab.' : 'Click to view record in a new tab.'}
                  onClick={(e) => {
                    if (e.button !== 0) return;
                    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
                    if (openParentInModal) {
                      e.preventDefault();
                      dispatchLookupPreviewOpen({
                        objectName: refObj,
                        recordId: lookupIdStr,
                      });
                    }
                  }}
                >
                  {lookupDisplay}
                </a>
                <a
                  href={viewHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-brand"
                  onClick={() => {
                    dispatchLookupPreviewClose();
                  }}
                  title="Open full record in a new tab"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    lineHeight: 1,
                    flexShrink: 0,
                    opacity: 0.9,
                    padding: '2px',
                  }}
                >
                  <i
                    className="fas fa-external-link-alt"
                    style={{ fontSize: '0.8em' }}
                    aria-hidden
                  />
                </a>
              </span>
            ) : (
              lookupDisplay
            );
        }
      }
    } else if (field.type === 'calculated') {
      displayValue = renderCalculatedField(field);
    } else if (field.type === 'date' && rawValue) {
      displayValue = formatDate(rawValue);
    } else if (field.type === 'datetime' && rawValue) {
      displayValue = formatDateTime(rawValue);
    } else if (field.type === 'time' && rawValue) {
      displayValue = formatTime(rawValue);
    } else if (field.type === 'checkbox' || field.type === 'boolean') {
      displayValue = renderReadOnlyCheckbox(rawValue);
    } else if (field.type === 'picklist' && rawValue) {
      // For picklist fields, show the displayValue (label) instead of the key
      displayValue = rawDisplayValue || rawValue;
    } else if (field.type === 'multipicklist' && rawValue) {
      // Use displayValue if available (from backend mapping), otherwise fallback to value formatting
      displayValue = rawDisplayValue
        ? String(rawDisplayValue).split(';').join(', ')
        : String(rawValue).split(';').join(', ');
    } else if (field.type === 'rollupsummary' && rawValue !== null) {
      // For rollup summary, check display_config for output type
      const displayConfig = field.displayConfig ?? field.display_config;
      const parsedConfig = parseDisplayConfig(displayConfig);
      const rollupConfig = ensurePlainObject(parsedConfig.rollupSummary);
      const outputType = (rollupConfig.outputType || 'number').toLowerCase();

      const numValue = Number(rawValue);
      if (!isNaN(numValue)) {
        if (outputType === 'currency') {
          // Format as currency
          const decimalPlaces =
            field.scale !== undefined &&
              field.scale !== null &&
              field.scale !== ''
              ? parseInt(field.scale, 10)
              : 2;
          const opts = {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          };
          const recordCurrency = getRecordFieldValue('currency');
          const currencyCode = recordCurrency || resolveCurrency('INR');
          displayValue = formatCurrency(numValue, currencyCode, opts);
          // Show base currency equivalent when record currency differs from base
          const recordExchangeRate = getRecordFieldValue('exchange_rate');
          if (
            recordCurrency &&
            displayBaseCurrencyCode &&
            String(recordCurrency).trim().toUpperCase() !== String(displayBaseCurrencyCode).trim().toUpperCase()
          ) {
            const rowRate = Number(recordExchangeRate);
            const effectiveRate = resolveEffectiveExchangeRate(
              recordCurrency,
              rowRate,
              displayExchangeRateLookup
            );
            if (Number.isFinite(effectiveRate) && effectiveRate > 0) {
              const baseEquivalent = numValue / effectiveRate;
              displayValue += ` (${formatCurrency(baseEquivalent, displayBaseCurrencyCode, opts)} ${displayBaseCurrencyCode})`;
            }
          }
        } else if (outputType === 'percent') {
          displayValue = formatPercentFieldValue(numValue, field);
        } else if (outputType === 'date') {
          // Format as date
          displayValue = formatDate(rawValue);
        } else if (outputType === 'datetime') {
          // Format as datetime
          displayValue = formatDateTime(rawValue);
        } else if (outputType === 'time') {
          // Format as time
          displayValue = formatTime(rawValue);
        } else {
          // Default to number formatting
          const options = {};
          if (
            field.scale !== undefined &&
            field.scale !== null &&
            field.scale !== ''
          ) {
            const decimalPlaces = parseInt(field.scale, 10);
            options.minimumFractionDigits = decimalPlaces;
            options.maximumFractionDigits = decimalPlaces;
          }
          displayValue = formatNumber(numValue, options);
        }
      }
    } else if (
      String(field.fieldName || '').toLowerCase() === 'exchange_rate' &&
      rawValue !== null &&
      rawValue !== undefined &&
      String(rawValue).trim() !== ''
    ) {
      if (!multiCurrencyEnabled) {
        displayValue = '—';
      } else {
        // Exchange rate must not use the currency-money branch (mis-typed layout as
        // data_type currency would show a misleading $/symbol amount). Show scaled
        // number + same "base context" pattern as currency fields.
        const numValue = Number(rawValue);
        if (!isNaN(numValue)) {
          const decimalPlaces =
            field.scale !== undefined &&
              field.scale !== null &&
              field.scale !== ''
              ? parseInt(field.scale, 10)
              : 4;
          const opts = {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
          };
          displayValue = formatNumber(numValue, opts);
          const recordCurrency = getRecordFieldValue('currency');
          if (
            recordCurrency &&
            baseCurrencyCode &&
            String(recordCurrency).trim().toUpperCase() !==
            String(baseCurrencyCode).trim().toUpperCase()
          ) {
            const rec = String(recordCurrency).trim();
            const base = String(baseCurrencyCode).trim();
            displayValue += ` (1 ${base} = ${formatNumber(numValue, opts)} ${rec})`;
          } else if (baseCurrencyCode) {
            displayValue += ` (${String(baseCurrencyCode).trim()})`;
          }
        }
      }
    } else if (
      (field.type === 'number' ||
        field.type === 'decimal') &&
      rawValue !== null
    ) {
      const numValue = Number(rawValue);
      if (!isNaN(numValue)) {
        // Use scale (decimal places) if provided
        const options = {};
        if (
          field.scale !== undefined &&
          field.scale !== null &&
          field.scale !== ''
        ) {
          const decimalPlaces = parseInt(field.scale, 10);
          options.minimumFractionDigits = decimalPlaces;
          options.maximumFractionDigits = decimalPlaces;
        }
        displayValue = formatNumber(numValue, options);
      }
    } else if (
      (field.type === 'percent' || resolvedFieldType === 'percent') &&
      rawValue !== null &&
      rawValue !== undefined &&
      String(rawValue).trim() !== ''
    ) {
      displayValue = formatPercentFieldValue(rawValue, field);
    } else if (field.type === 'currency' && rawValue !== null) {
      const numValue = Number(rawValue);
      if (!isNaN(numValue)) {
        // Use scale (decimal places) if provided, otherwise default to 2
        const decimalPlaces =
          field.scale !== undefined &&
            field.scale !== null &&
            field.scale !== ''
            ? parseInt(field.scale, 10)
            : 2;
        const opts = {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        };
        // Resolve currency: record currency > token currency (user or company) > default
        const recordCurrency = getRecordFieldValue('currency');
        const currencyCode = recordCurrency || resolveCurrency('INR');
        displayValue = formatCurrency(numValue, currencyCode, opts);
        // If record currency differs from base currency, show base equivalent e.g. "92000 ($1000 USD)"
        const recordExchangeRate = getRecordFieldValue('exchange_rate');
        if (
          recordCurrency &&
          displayBaseCurrencyCode &&
          String(recordCurrency).trim().toUpperCase() !== String(displayBaseCurrencyCode).trim().toUpperCase()
        ) {
          const rowRate = Number(recordExchangeRate);
          const effectiveRate = resolveEffectiveExchangeRate(
            recordCurrency,
            rowRate,
            displayExchangeRateLookup
          );
          if (Number.isFinite(effectiveRate) && effectiveRate > 0) {
            const baseEquivalent = numValue / effectiveRate;
            displayValue += ` (${formatCurrency(baseEquivalent, displayBaseCurrencyCode, opts)} ${displayBaseCurrencyCode})`;
          }
        }
      }
    } else if (isRichTextField) {
      const html =
        rawValue != null && String(rawValue).trim() !== ''
          ? decodeHtmlEntitiesForDisplay(String(rawValue))
          : '';
      displayValue = html ? (
        <div
          className='richtext-value rich-text-content'
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        '—'
      );
    } else if (field.type === 'phone' && rawValue) {
      displayValue = enableCti ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <span>{String(rawValue)}</span>
          <button
            type='button'
            className='btn btn-link p-0'
            title='Call this number'
            onClick={() => handleTelecmiCallForNumber(rawValue)}
            style={{ textDecoration: 'none' }}
          >
            <i className='fas fa-phone' />
          </button>
        </span>
      ) : (
        String(rawValue)
      );
    } else if (field.type === 'barcode' && rawValue) {
      const displayConfig = field.displayConfig ?? field.display_config;
      const { merged: config } = flattenDisplayConfig(displayConfig);
      const format = typeof config.format === 'string' && config.format.trim() !== ''
        ? config.format.trim().toUpperCase()
        : 'CODE128';
      const width = toFiniteNumber(config.width) ?? 2;
      const height = toFiniteNumber(config.height) ?? 100;
      const displayBarcodeValue = config.displayValue === false || config.displayValue === 'false' || config.displayValue === 0 || config.displayValue === '0'
        ? false
        : true;

      displayValue = (
        <Barcode
          value={String(rawValue)}
          format={format}
          width={width ?? 2}
          height={height ?? 100}
          displayValue={displayBarcodeValue}
        />
      );
    } else if (field.type === 'qrcode' && rawValue) {
      const displayConfig = field.displayConfig ?? field.display_config;
      const { merged: config } = flattenDisplayConfig(displayConfig);
      const size = toFiniteNumber(config.size) ?? 200;
      const levelRaw = typeof config.errorCorrectionLevel === 'string'
        ? config.errorCorrectionLevel.toUpperCase()
        : '';
      const validLevels = ['L', 'M', 'Q', 'H'];
      const level = validLevels.includes(levelRaw) ? levelRaw : 'M';

      displayValue = (
        <QRCode
          value={String(rawValue)}
          size={size ?? 200}
          level={level}
          style={{ maxWidth: "100%", height: "auto" }}
        />
      );
    } else if (field.type === 'image' && rawValue) {
      const displayConfig = field.displayConfig ?? field.display_config;
      const { merged: config } = flattenDisplayConfig(displayConfig);
      const isBrowser = typeof window !== 'undefined';
      const isMobile = isBrowser ? window.innerWidth <= 768 : false;
      const resolveDimension = (primaryKey, secondaryKey, fallback) => {
        const primary = toFiniteNumber(config[primaryKey]);
        if (primary !== null) {
          return primary;
        }
        if (secondaryKey) {
          const secondary = toFiniteNumber(config[secondaryKey]);
          if (secondary !== null) {
            return secondary;
          }
        }
        return fallback;
      };

      const width = isMobile
        ? resolveDimension('mobileWidth', 'width', 150)
        : resolveDimension('width', null, 300);
      const height = isMobile
        ? resolveDimension('mobileHeight', 'height', 150)
        : resolveDimension('height', null, 300);
      const allowedFits = ['contain', 'cover', 'fill', 'scale-down', 'none'];
      const fitRaw = typeof config.objectFit === 'string' ? config.objectFit.toLowerCase() : '';
      const objectFit = allowedFits.includes(fitRaw) ? fitRaw : 'contain';

      displayValue = (
        <img
          src={resolveImageSrc(rawValue)}
          alt={field.label}
          style={{
            width: width !== null ? `${width}px` : 'auto',
            height: height !== null ? `${height}px` : 'auto',
            maxWidth: '100%',
            objectFit,
            borderRadius: '4px',
            border: '1px solid #e0e0e0'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentNode.innerHTML = '<span style="color: #999;">Image not available</span>';
          }}
        />
      );
    }

    const fieldEditable =
      !recordData?.is_history_object &&
      !embedDisableEdit &&
      detailLayoutIncludesEditAction &&
      field.isReadonly !== true &&
      field.is_readonly !== true;

    const openFieldEdit = () => {
      if (enableInlineEditing && detailFieldSupportsQuickEdit(field)) {
        setSingleFieldEditName(String(field.fieldName));
      } else {
        setShowEditModal(true);
      }
    };

    return (
      <div className={`detail-item${fieldEditable ? ' detail-item--editable' : ''}`}>
        <div className='label-value-group'>
          <div className='detail-field-label'>
            {field.label ?? field.field_label}
          </div>
          <div
            className={isRichTextField ? 'detail-field-value value--richtext' : 'detail-field-value'}
          >
            {displayValue}
          </div>
        </div>
        {fieldEditable && (
          <button
            type='button'
            className='edit-icon'
            title={`Edit ${field.label ?? field.field_label ?? 'field'}`}
            aria-label={`Edit ${field.label ?? field.field_label ?? 'field'}`}
            onClick={(e) => {
              e.currentTarget.blur();
              openFieldEdit();
            }}
          >
            <i className='fas fa-pen' aria-hidden />
          </button>
        )}
      </div>
    );
  };

  const renderSection = (section, sectionIndex = 0) => {
    // Get the number of columns for this section from the database
    const sectionColumns = section.layoutColumns || section.layout_columns || 2; // Default to 2 columns

    console.log(
      `Rendering section "${section.sectionName}" with ${sectionColumns} columns`
    );
    console.log('Section fields:', section.fields);

    // Get task type from record data for field visibility filtering
    const taskType =
      objectName === 'task'
        ? recordData?.details
          ?.flatMap(s => s.fields)
          ?.find(
            f =>
              f.fieldName === 'task_type' ||
              f.fieldName === 'tasktype' ||
              f.fieldName === 'type'
          )?.value
        : null;

    // Get mail config type for field visibility (SMTP, Outlook, Google)
    const mailConfigType =
      objectName === 'mail_config'
        ? recordData?.details
          ?.flatMap(s => s.fields)
          ?.find(
            f =>
              (f.fieldName && (f.fieldName === 'type' || f.fieldName === 'Type'))
          )?.value
        : null;

    const filteredFields = filterDetailLayoutFields(section.fields, {
      taskType,
      mailConfigType,
    });

    // If no visible fields after filtering, don't render the section
    if (filteredFields.length === 0) {
      console.log(
        `Hiding empty section: ${section.sectionLabel || section.sectionName}`
      );
      return null;
    }

    // Create rows based on field column spans and section layout
    const rows = [];
    let currentRow = [];
    let currentRowWidth = 0;

    filteredFields.forEach(field => {
      // Calculate field width based on column span
      let fieldWidth;
      if (field.columnSpan === 2) {
        // Full width field (spans 2 columns in the section's layout)
        fieldWidth = Math.ceil(12 / sectionColumns) * 2;
      } else {
        // Regular field (spans 1 column)
        fieldWidth = Math.ceil(12 / sectionColumns);
      }

      console.log(
        `Field "${field.label}" (${field.fieldName}) - columnSpan: ${field.columnSpan}, calculated width: ${fieldWidth}`
      );

      // Check if this field would exceed the row width
      if (currentRowWidth + fieldWidth > 12) {
        // Start a new row
        if (currentRow.length > 0) {
          rows.push([...currentRow]);
          console.log('Starting new row, current row width:', currentRowWidth);
        }
        currentRow = [];
        currentRowWidth = 0;
      }

      // Add field to current row
      currentRow.push(field);
      currentRowWidth += fieldWidth;
    });

    // Add any remaining fields
    if (currentRow.length > 0) {
      rows.push([...currentRow]);
    }

    console.log('Final rows structure:', rows);

    const sectionKey = `${String(section.sectionName)}::${sectionIndex}`;
    const sectionBodyId = `detail-section-body-${sectionIndex}`;
    const sectionHdrId = `detail-section-hdr-${sectionIndex}`;
    const sectionCollapsed = collapsedDetailSections.has(sectionKey);
    const altBand = sectionIndex % 2;
    const sectionAccent =
      altBand === 0 ? theme.colors.primary : theme.colors.borderMedium;
    const headerBg =
      altBand === 0 ? 'rgba(36, 55, 115, 0.07)' : 'rgba(148, 163, 184, 0.12)';

    return (
      <div
        className='mb-3 detail-section-card'
        key={`${String(section.sectionName)}-${sectionIndex}`}
        style={{
          border: `1px solid ${theme.colors.borderLight}`,
          borderLeft: `4px solid ${sectionAccent}`,
          borderRadius: '8px',
        }}
      >
        <button
          type='button'
          className='detail-section-header w-100 d-flex align-items-center gap-2 text-start border-0'
          onClick={() => {
            setCollapsedDetailSections((prev) => {
              const next = new Set(prev);
              if (next.has(sectionKey)) next.delete(sectionKey);
              else next.add(sectionKey);
              return next;
            });
          }}
          aria-expanded={!sectionCollapsed}
          aria-controls={sectionBodyId}
          id={sectionHdrId}
          style={{
            fontFamily: theme.typography.fontFamily.primary,
            backgroundColor: headerBg,
            padding: '10px 14px',
            cursor: 'pointer',
          }}
        >
          <i
            className={`fas fa-chevron-down detail-section-chevron${sectionCollapsed ? ' is-collapsed' : ''
              }`}
            style={{ fontSize: '0.7rem', color: theme.colors.textSecondary }}
            aria-hidden
          />
          <h4
            className='mb-0 flex-grow-1'
            style={{
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.colors.textPrimary,
              lineHeight: theme.typography.lineHeight.tight,
              fontSize: theme.typography.fontSize['2xl'],
            }}
          >
            {section.sectionLabel || section.sectionName}
          </h4>
        </button>
        <Collapse in={!sectionCollapsed}>
          <div id={sectionBodyId} role='region' aria-labelledby={sectionHdrId}>
            <div
              className='detail-grid'
              style={{
                gridTemplateColumns: `repeat(${sectionColumns}, 1fr)`,
                gap: '16px 24px',
                padding: '12px 16px 16px 16px',
              }}
            >
              {rows.map((fieldsInRow, rowIdx) => (
                <React.Fragment key={rowIdx}>
                  {fieldsInRow.map((field, fIdx) => (
                    <div
                      key={
                        field.fieldName
                          ? `${field.fieldName}-${rowIdx}-${fIdx}`
                          : `${section.sectionName}-${rowIdx}-${fIdx}`
                      }
                      style={{
                        gridColumn:
                          field.columnSpan === 2
                            ? `span ${Math.min(2, sectionColumns)}`
                            : 'span 1',
                      }}
                    >
                      {renderDetailItem(field)}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Collapse>
      </div>
    );
  };

  if (isLoaded && error) {
    return (
      <div className='d-flex justify-content-center align-items-center w-100'>
        <div
          className='alert alert-danger text-center shadow'
          style={{ width: '70%', maxWidth: '400px' }}
        >
          <h5 className='mb-2'>Oops!</h5>
          <p className='mb-0'>{error || 'No Record Found!'}</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className='d-flex flex-column justify-content-center align-items-center w-100 px-3'
        style={{
          minHeight: hideEmbedActionBar ? 'min(240px, 42vh)' : 'min(360px, 58vh)',
          fontFamily: theme.typography.fontFamily.primary,
        }}
        role='status'
        aria-live='polite'
        aria-busy='true'
      >
        <div
          className='spinner-border text-primary mb-3'
          style={{
            width: '2.75rem',
            height: '2.75rem',
            borderWidth: '0.22rem',
          }}
        />
        <span
          className='text-muted'
          style={{
            fontSize: theme.typography.fontSize.base,
          }}
        >
          Loading record…
        </span>
        <span className='visually-hidden'>Loading</span>
      </div>
    );
  }

  const BLANK_FIELDS_TOGGLE_MODES = [
    {
      mode: 'all',
      label: 'Show All',
      icon: 'fas fa-list',
      title: 'Showing all fields. Click to hide blank fields.',
    },
    {
      mode: 'hide_blank',
      label: 'Hide Blank Fields',
      icon: 'fas fa-eye-slash',
      title: 'Blank fields hidden. Click to show only blank fields.',
    },
    {
      mode: 'show_blank',
      label: 'Show Blank Fields',
      icon: 'fas fa-eye',
      title: 'Showing only blank fields. Click to show all fields.',
    },
  ];

  const isBlankFieldsToggleAction = (action) => {
    const key = String(action?.action || action?.type || '').toLowerCase();
    // Current key + legacy three-button keys if any layout still has them
    return (
      key === 'toggle_blank_fields' ||
      key === 'show_all' ||
      key === 'show_blank_fields' ||
      key === 'hide_blank_fields'
    );
  };

  const cycleBlankFieldsMode = () => {
    setBlankFieldsMode((prev) => {
      const idx = BLANK_FIELDS_TOGGLE_MODES.findIndex((m) => m.mode === prev);
      const next = BLANK_FIELDS_TOGGLE_MODES[(idx + 1) % BLANK_FIELDS_TOGGLE_MODES.length];
      return next.mode;
    });
  };

  const renderBlankFieldsToggleButton = (action, idx, isDropdownItem = false) => {
    const currentIdx = Math.max(
      0,
      BLANK_FIELDS_TOGGLE_MODES.findIndex((m) => m.mode === blankFieldsMode)
    );
    const current = BLANK_FIELDS_TOGGLE_MODES[currentIdx];
    const next =
      BLANK_FIELDS_TOGGLE_MODES[(currentIdx + 1) % BLANK_FIELDS_TOGGLE_MODES.length];
    const isFiltered = blankFieldsMode !== 'all';
    // Label shows what the next click will do (same pattern as the old 2-state toggle)
    const label = next.label;
    const iconClass = action?.icon || next.icon;
    const buttonContent = (
      <>
        {iconClass ? <i className={iconClass} aria-hidden /> : null}
        {label}
      </>
    );
    const className = `detail-action-button detail-action-toggle${isFiltered ? ' is-active' : ''}${
      isDropdownItem ? ' w-100 text-start' : ''
    }`;

    if (isDropdownItem) {
      return (
        <div key={idx} className="p-1">
          <button
            type="button"
            className={className}
            onClick={cycleBlankFieldsMode}
            title={current.title}
            aria-pressed={isFiltered}
            aria-label={label}
          >
            {buttonContent}
          </button>
        </div>
      );
    }

    return (
      <button
        key={idx}
        type="button"
        className={className}
        onClick={cycleBlankFieldsMode}
        title={current.title}
        aria-pressed={isFiltered}
        aria-label={label}
      >
        {buttonContent}
      </button>
    );
  };

  return (
    <div
      className={
        hideEmbedActionBar ? 'account-page px-2 py-1' : 'account-page px-3 py-1'
      }
    >
      {/* Header */}
      <div className='sf-header'>
        {!hideEmbedActionBar && (
          <div className='sf-top'>
            <div className='sf-title'>
              <i className='fas fa-building'></i>
              <div>
                <div style={{ fontSize: '13px' }}>
                  <a
                    href={`/listview/${objectName}`}
                    style={{
                      color: '#5c5c5c',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = theme.colors.primary;
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#5c5c5c';
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    {detailPageObjectLabel}
                  </a>
                </div>
                <h2
                  style={{
                    fontFamily: theme.typography.fontFamily.primary,
                    fontWeight: theme.typography.fontWeight.bold,
                    color: theme.colors.textSecondary,
                    fontSize: theme.typography.fontSize['3xl'], // Increased from default to 3xl (1.875rem)
                  }}
                >
                  {getNameFromDetails() || 'Detail'}
                </h2>
              </div>
            </div>
            <div className='sf-actions'>
              {objectName === 'email_campaigns' &&
                (() => {
                  return (

                    <button
                      className='detail-action-button'
                      onClick={() => setShowAddAudience(true)}
                    >
                      <i className='fas fa-add'></i>
                      Add Audience
                    </button>
                  );
                })()}

              {/* Dynamic Action Buttons from Layout Config - Only show if actions are configured */}
              {console.log('recordData?.header?.actions: ', recordData?.header?.actions)}
              {recordData?.header?.actions && recordData?.header?.actions.length > 0 && (() => {
                // Helper function to render an action button
                const renderActionButton = (action, idx, isDropdownItem = false) => {
                  if (isBlankFieldsToggleAction(action)) {
                    return renderBlankFieldsToggleButton(action, idx, isDropdownItem);
                  }
                  // Skip edit and new actions for email_log
                  if (objectName?.toLowerCase() === 'email_log' && (action.action === 'edit' || action.action === 'new')) {
                    return null;
                  }
                  // Do not show Email action for email_template / email_layout (config records, not send-from targets)
                  const setupEmailObj = objectName?.toLowerCase();
                  if (
                    (setupEmailObj === 'email_template' || setupEmailObj === 'email_layout') &&
                    action.action === 'email'
                  ) {
                    return null;
                  }
                  if (embedDisableEdit && action.action === 'edit') {
                    return null;
                  }
                  if (!canEditStandardRecordFlag && (action.action === 'edit' || action.action === 'clone')) {
                    return null;
                  }
                  if (!canDeleteStandardRecordFlag && action.action === 'delete') {
                    return null;
                  }

                  // Handle edit action
                  if (action.action === 'edit') {
                    const buttonContent = (
                      <>
                        {action.icon && <i className={action.icon}></i>}
                        {action.label || 'Edit'}
                      </>
                    );
                    const openEditModal = () => setShowEditModal(true);

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            className="detail-action-button w-100 text-start"
                            onClick={openEditModal}
                            title={action.label || 'Edit this record'}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={idx}
                        className="detail-action-button"
                        onClick={openEditModal}
                        title={action.label || 'Edit this record'}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  // Handle clone action
                  if (action.action === 'clone') {
                    // Hide clone for history objects
                    if (recordData?.is_history_object) return null;

                    const buttonContent = (
                      <>
                        {action.icon && <i className={action.icon}></i>}
                        {action.label || 'Clone'}
                      </>
                    );

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            className="detail-action-button w-100 text-start"
                            onClick={() => setShowCloneModal(true)}
                            title={action.label || 'Clone this record'}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={idx}
                        className="detail-action-button"
                        onClick={() => setShowCloneModal(true)}
                        title={action.label || 'Clone this record'}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  // Handle new action
                  if (action.action === 'new') {
                    const buttonContent = (
                      <>
                        {action.icon && <i className={action.icon}></i>}
                        {action.label || 'New'}
                      </>
                    );

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            className="detail-action-button w-100 text-start"
                            onClick={() => setShowNewRecordModal(true)}
                            title={action.label || 'Create new record'}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={idx}
                        className="detail-action-button"
                        onClick={() => setShowNewRecordModal(true)}
                        title={action.label || 'Create new record'}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  // Handle delete action (not allowed for user object)
                  if (action.action === 'delete') {
                    if (objectName?.toLowerCase() === 'user') return null;
                    const buttonContent = (
                      <>
                        {isDeleting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Deleting...
                          </>
                        ) : (
                          <>
                            {action.icon && <i className={action.icon}></i>}
                            {action.label || 'Delete'}
                          </>
                        )}
                      </>
                    );

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            className="detail-action-button delete-button w-100 text-start"
                            onClick={handleDeleteRecord}
                            disabled={isDeleting}
                            title={action.label || 'Delete this record'}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={idx}
                        className="detail-action-button delete-button"
                        onClick={handleDeleteRecord}
                        disabled={isDeleting}
                        title={action.label || 'Delete this record'}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  // Handle generate_document action
                  if (action.action === 'generate_document') {
                    const templateAction = action.templateAction || 'customer quote';
                    const templateId = action.templateId || null;
                    const createAttachment = action.createAttachment || false;
                    const isGenerating = generatingDocument === idx && !docImageUploadModal;

                    const buttonContent = isGenerating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        {action.icon && <i className={action.icon}></i>}
                        {action.label || 'Generate Document'}
                      </>
                    );

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            className="detail-action-button w-100 text-start"
                            onClick={async () => {
                              try {
                                setGeneratingDocument(idx);
                                setToast({
                                  show: true,
                                  message: `Generating ${action.label || 'Document'}...`,
                                  variant: 'primary',
                                });
                                const result = await executeDetailLayoutDocumentGeneration(
                                  templateAction,
                                  createAttachment,
                                  idx,
                                  action.label || 'Document',
                                  templateId
                                );
                                if (!result?.needsUpload) {
                                  setToast({
                                    show: true,
                                    message: `${action.label || 'Document'} generated and downloaded successfully!${createAttachment ? ' Document has been attached to this record.' : ''}`,
                                    variant: 'success',
                                  });
                                }
                              } catch (error) {
                                console.error('Error generating document:', error);
                                setToast({
                                  show: true,
                                  message: error.message || 'Failed to generate document. Please ensure a document template is configured.',
                                  variant: 'danger',
                                });
                              } finally {
                                setGeneratingDocument(null);
                              }
                            }}
                            title={action.label || 'Generate Document'}
                            disabled={isGenerating}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={idx}
                        className="detail-action-button"
                        onClick={async () => {
                          try {
                            setGeneratingDocument(idx);
                            setToast({
                              show: true,
                              message: `Generating ${action.label || 'Document'}...`,
                              variant: 'primary',
                            });
                            const result = await executeDetailLayoutDocumentGeneration(
                              templateAction,
                              createAttachment,
                              idx,
                              action.label || 'Document',
                              templateId
                            );
                            if (!result?.needsUpload) {
                              setToast({
                                show: true,
                                message: `${action.label || 'Document'} generated and downloaded successfully!${createAttachment ? ' Document has been attached to this record.' : ''}`,
                                variant: 'success',
                              });
                            }
                          } catch (error) {
                            console.error('Error generating document:', error);
                            setToast({
                              show: true,
                              message: error.message || 'Failed to generate document. Please ensure a document template is configured.',
                              variant: 'danger',
                            });
                          } finally {
                            setGeneratingDocument(null);
                          }
                        }}
                        title={action.label || 'Generate Document'}
                        disabled={isGenerating}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  // add_products and add_services use the same line-item wizard (labels differ per configured action)
                  if (action.action === 'add_products' || action.action === 'add_services') {
                    if (!['opportunity', 'quote', 'order'].includes(objectName?.toLowerCase())) {
                      return null;
                    }

                    const handleClick =
                      objectName?.toLowerCase() === 'opportunity'
                        ? handleAddProducts
                        : objectName?.toLowerCase() === 'quote'
                          ? handleAddQuoteProducts
                          : handleAddOrderProducts;

                    const defaultLabel =
                      action.action === 'add_services' ? 'Add Services' : 'Add Products';
                    const defaultTitle =
                      action.action === 'add_services'
                        ? 'Add services to this record'
                        : 'Add products to this record';

                    const buttonContent = (
                      <>
                        {action.icon && <i className={action.icon}></i>}
                        {action.label || defaultLabel}
                      </>
                    );

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            className="detail-action-button w-100 text-start"
                            onClick={handleClick}
                            title={action.label || defaultTitle}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={idx}
                        className="detail-action-button"
                        onClick={handleClick}
                        title={action.label || defaultTitle}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  // Handle sync_products action
                  if (action.action === 'sync_products') {
                    // Only show for Quote objects
                    if (objectName?.toLowerCase() !== 'quote') {
                      return null;
                    }

                    const opportunityId = getRecordFieldValue('opportunity');
                    // Only show if quote is linked to an opportunity
                    if (!opportunityId) {
                      return null;
                    }

                    const buttonContent = syncingProducts ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        {action.label || 'Syncing...'}
                      </>
                    ) : (
                      <>
                        {action.icon && <i className={action.icon}></i>}
                        {action.label || 'Sync Products'}
                      </>
                    );

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            className="detail-action-button w-100 text-start"
                            onClick={handleSyncProducts}
                            disabled={syncingProducts}
                            title={action.label || 'Sync products from quote to opportunity'}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={idx}
                        className="detail-action-button"
                        onClick={handleSyncProducts}
                        disabled={syncingProducts}
                        title={action.label || 'Sync products from quote to opportunity'}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  // Handle convert_lead action (only for lead object, only when not converted)
                  if (action.action === 'convert_lead') {
                    if (objectName?.toLowerCase() !== 'lead') return null;
                    const isConverted =
                      recordData?.details?.some(section =>
                        section.fields?.some(
                          field =>
                            field.fieldName === 'is_converted' &&
                            (field.value === true || field.value === 'true')
                        )
                      ) ||
                      recordData?.otherHiddenFields?.some(
                        field =>
                          field.fieldName === 'is_converted' &&
                          (field.value === true || field.value === 'true')
                      );
                    if (isConverted || statusHidesLeadConvert) return null;

                    const buttonContent = (
                      <>
                        {action.icon && <i className={action.icon}></i>}
                        {action.label || 'Convert Lead'}
                      </>
                    );

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            className="detail-action-button w-100 text-start"
                            onClick={() => setShowLeadConvertModal(true)}
                            title={action.label || 'Convert this lead'}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={idx}
                        className="detail-action-button"
                        onClick={() => setShowLeadConvertModal(true)}
                        title={action.label || 'Convert this lead'}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  // Handle generate_quote_from_lead: generate quote document when on a lead (creates quote from lead, then generates PDF)
                  if (action.action === 'generate_quote_from_lead') {
                    if (objectName?.toLowerCase() !== 'lead') return null;

                    const isGenerating = generatingDocument === idx && !docImageUploadModal;
                    const buttonContent = isGenerating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        {action.icon && <i className={action.icon}></i>}
                        {action.label || 'Generate Quote'}
                      </>
                    );

                    const handleGenerateQuoteFromLead = async () => {
                      if (!id) {
                        setToast({ show: true, message: 'Lead ID is missing.', variant: 'danger' });
                        return;
                      }
                      try {
                        setGeneratingDocument(idx);
                        setToast({ show: true, message: 'Generating quote from lead...', variant: 'info' });
                        await GenerateDocumentApi.generateQuoteFromLead(id, action.templateAction || 'customer quote');
                        setToast({ show: true, message: 'Quote generated and downloaded successfully!', variant: 'success' });
                      } catch (error) {
                        console.error('Error generating quote from lead:', error);
                        setToast({
                          show: true,
                          message: error.message || 'Failed to generate quote. Ensure a quote document template is configured.',
                          variant: 'danger',
                        });
                      } finally {
                        setGeneratingDocument(null);
                      }
                    };

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            className="detail-action-button w-100 text-start"
                            onClick={handleGenerateQuoteFromLead}
                            title={action.label || 'Generate quote from this lead'}
                            disabled={isGenerating}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }
                    return (
                      <button
                        key={idx}
                        className="detail-action-button"
                        onClick={handleGenerateQuoteFromLead}
                        title={action.label || 'Generate quote from this lead'}
                        disabled={isGenerating}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  if (action.action === 'custom_action') {
                    const resolvedUrl = interpolateDetailCustomActionUrl(
                      action.target_url || '',
                      objectName,
                      id
                    );
                    const isComponentAction =
                      String(action.action_behavior || '').toLowerCase() === 'component' &&
                      action.component_name;
                    const isCustomScriptAction =
                      String(action.action_behavior || '').toLowerCase() === 'custom_script' &&
                      action.action_id;
                    const isWebComponentBehavior = (() => {
                      const ab = String(action.action_behavior || '').toLowerCase();
                      return ab === 'view_page' || ab === 'web_component' || ab === 'ui_schema';
                    })();
                    const isWebComponentLoading =
                      isWebComponentBehavior && loadingWebComponentActionId === action.action_id;
                    const isExecutingScript = executingCustomScriptId === action.action_id;
                    const buttonContent = (
                      <>
                        {action.icon && <i className={action.icon}></i>}
                        {isCustomScriptAction && isExecutingScript
                          ? 'Processing...'
                          : isWebComponentLoading
                            ? 'Loading...'
                            : action.label || action.action_name || 'Action'}
                      </>
                    );

                    const runCustomDetailAction = async () => {
                      if (isComponentAction) {
                        const { Component, blockedReason } = resolveRegisteredTabComponent(
                          action.component_name,
                          uiTabComponentAllowlist
                        );
                        if (!Component) {
                          setToast({
                            show: true,
                            message:
                              blockedReason === 'not_activated'
                                ? `Component "${action.component_name}" is not enabled in component_registry for this organization.`
                                : `Component "${action.component_name}" is not available in this application build.`,
                            variant: 'warning',
                          });
                          return;
                        }
                        const mapped = buildDetailCustomActionProps(action, {
                          layoutObjectName: objectName,
                          getFieldValueForMapping: getRelationshipFilterValue,
                        });
                        setCustomDetailActionComponent({
                          show: true,
                          title: action.label || action.action_name || 'Action',
                          Component,
                          componentProps: {
                            recordData,
                            objectName,
                            recordId: id,
                            onRefresh: refreshRecordData,
                            ...mapped,
                          },
                        });
                        return;
                      }

                      if (isWebComponentBehavior) {
                        await openWebComponentModalForAction(action);
                        return;
                      }

                      if (isCustomScriptAction) {
                        setExecutingCustomScriptId(action.action_id);
                        try {
                          const url = `${constants.API_BASE_URL}/api/custom-scripts/execute`;
                          const res = await axios.post(
                            url,
                            {
                              actionId: action.action_id,
                              recordId: id,
                              objectName,
                            },
                            {
                              headers: {
                                ...crmAuthHeaders(),
                                'Content-Type': 'application/json',
                              },
                            }
                          );
                          const data = res.data;
                          if (data.success) {
                            const payload = data.data;
                            const toastVariant = normalizeCustomScriptToastVariant(payload);
                            const redirectUrl = resolveCustomScriptRedirectUrl(payload);
                            const toastMessage = data.message || 'Script completed.';
                            if (redirectUrl) {
                              stashPendingRedirectToast({
                                message: toastMessage,
                                variant: toastVariant,
                              });
                            } else {
                              setToast({
                                show: true,
                                message: toastMessage,
                                variant: toastVariant,
                              });
                            }
                            const shouldRefresh =
                              !redirectUrl &&
                              payload &&
                              typeof payload === 'object' &&
                              payload.refreshRecord === true;
                            if (shouldRefresh) {
                              await refreshRecordData();
                            }
                            if (redirectUrl) {
                              window.location.assign(redirectUrl);
                            }
                          } else {
                            setToast({
                              show: true,
                              message: data.message || 'Script failed.',
                              variant: 'danger',
                            });
                          }
                        } catch (error) {
                          setToast({
                            show: true,
                            message:
                              error?.userMessage ??
                              getApiErrorMessage(
                                error,
                                'Script execution failed.'
                              ),
                            variant: 'danger',
                          });
                        } finally {
                          setExecutingCustomScriptId(null);
                        }
                        return;
                      }

                      if (String(action.action_behavior || '').toLowerCase() === 'custom_script' && !action.action_id) {
                        setToast({
                          show: true,
                          message:
                            'This custom script action is missing its action id. Reload the page or re-save the layout.',
                          variant: 'warning',
                        });
                        return;
                      }

                      if (!resolvedUrl || !String(resolvedUrl).trim()) {
                        setToast({
                          show: true,
                          message: 'This action has no target URL configured.',
                          variant: 'warning',
                        });
                        return;
                      }
                      if (action.open_in_modal) {
                        setCustomDetailActionFrame({
                          show: true,
                          url: resolvedUrl,
                          title: action.label || action.action_name || 'Action',
                        });
                      } else if (/^https?:\/\//i.test(resolvedUrl)) {
                        window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
                      } else {
                        window.location.href = resolvedUrl;
                      }
                    };

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            type="button"
                            className="detail-action-button w-100 text-start"
                            onClick={runCustomDetailAction}
                            title={action.label || action.action_name || ''}
                            disabled={Boolean(
                              (isCustomScriptAction && isExecutingScript) || isWebComponentLoading
                            )}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }
                    return (
                      <button
                        key={idx}
                        type="button"
                        className="detail-action-button"
                        onClick={runCustomDetailAction}
                        title={action.label || action.action_name || ''}
                        disabled={Boolean(
                          (isCustomScriptAction && isExecutingScript) || isWebComponentLoading
                        )}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  // Handle WhatsApp action (configured per layout)
                  if ((typeof action === 'string' ? action : (action?.action ?? action?.type)) === 'whatsapp') {
                    if (!enableWatConnect) {
                      console.log('[DetailLayout][WhatsApp][debug] blocked: enableWatConnect=false', {
                        objectName,
                        id,
                        action,
                        decodedTokenEnable: decodedToken?.enable_watconnect,
                      });
                      return null;
                    }
                    const candidates = resolveWhatsAppCandidatesFromAction(action);
                    const fallbackPhone = defaultWhatsAppRecipientPhone;
                    const resolvedCandidates =
                      candidates.length > 0
                        ? candidates
                        : (fallbackPhone && String(fallbackPhone).trim() !== ''
                          ? [{ fieldName: 'phone', label: 'Phone', value: String(fallbackPhone) }]
                          : []);

                    if (resolvedCandidates.length === 0) {
                      console.log('[DetailLayout][WhatsApp][debug] blocked: no phone candidates', {
                        objectName,
                        id,
                        action,
                        candidates,
                        fallbackPhone,
                        defaultWhatsAppRecipientPhone,
                      });
                      return null;
                    }

                    const waLabel = action.label || 'WhatsApp';
                    const iconClass = typeof action === 'object' ? (action.icon || '') : '';
                    const iconLooksLikeWhatsApp =
                      iconClass &&
                      String(iconClass).toLowerCase().includes('whatsapp');
                    const showDefaultWhatsAppIcon = !iconLooksLikeWhatsApp;

                    const openChatWith = (phone) => {
                      console.log('[DetailLayout][WhatsApp][debug] opening chat', {
                        objectName,
                        id,
                        phone,
                        waLabel,
                      });
                      setWhatsAppRecipientPhone(String(phone));
                      setShowWhatsAppChat(true);
                    };

                    // If this action is rendered inside the "More" dropdown list, keep it simple:
                    // - 1 number: single button
                    // - many numbers: render a list of number options
                    if (isDropdownItem) {
                      if (resolvedCandidates.length === 1) {
                        return (
                          <div key={idx} className="p-1">
                            <button
                              className="detail-action-button w-100 text-start"
                              onClick={() => openChatWith(resolvedCandidates[0].value)}
                              title={waLabel}
                            >
                              {action.icon && <i className={action.icon}></i>}
                              {showDefaultWhatsAppIcon && <i className="fab fa-whatsapp"></i>}
                              {waLabel}
                            </button>
                          </div>
                        );
                      }
                      return (
                        <div key={idx} className="p-1">
                          <div className="small text-muted px-2 pt-1">{waLabel}</div>
                          {resolvedCandidates.map((c) => (
                            <button
                              key={`${idx}:${c.fieldName}:${c.value}`}
                              className="detail-action-button w-100 text-start"
                              onClick={() => openChatWith(c.value)}
                              title={`${c.label}: ${c.value}`}
                            >
                              {showDefaultWhatsAppIcon && <i className="fab fa-whatsapp"></i>}
                              {c.label}: {c.value}
                            </button>
                          ))}
                        </div>
                      );
                    }

                    // In the main header area: if multiple values, show a dropdown.
                    if (resolvedCandidates.length > 1) {
                      return (
                        <Dropdown key={idx} as="span">
                          <Dropdown.Toggle className="detail-action-button" title={waLabel}>
                            {action.icon && <i className={action.icon}></i>}
                            {showDefaultWhatsAppIcon && <i className="fab fa-whatsapp"></i>}
                            {waLabel}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            {resolvedCandidates.map((c) => (
                              <Dropdown.Item
                                key={`${idx}:${c.fieldName}:${c.value}`}
                                onClick={() => openChatWith(c.value)}
                                title={`${c.label}: ${c.value}`}
                              >
                                {c.label}: {c.value}
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      );
                    }

                    // Single number: keep as a normal button.
                    return (
                      <button
                        key={idx}
                        className="detail-action-button"
                        onClick={() => openChatWith(resolvedCandidates[0].value)}
                        title={waLabel}
                      >
                        {action.icon && <i className={action.icon}></i>}
                        {showDefaultWhatsAppIcon && <i className="fab fa-whatsapp"></i>}
                        {waLabel}
                      </button>
                    );
                  }

                  // Handle call action
                  if (action.action === 'call') {
                    if (!enableCti) {
                      return null;
                    }
                    const buttonContent = (
                      <>
                        {action.icon && <i className={action.icon}></i>}
                        {action.label || 'Call'}
                      </>
                    );

                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <button
                            className="detail-action-button w-100 text-start"
                            onClick={handleTelecmiCall}
                            title={action.label || 'Initiate call'}
                          >
                            {buttonContent}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={idx}
                        className="detail-action-button"
                        onClick={handleTelecmiCall}
                        title={action.label || 'Initiate call'}
                      >
                        {buttonContent}
                      </button>
                    );
                  }

                  // Handle email action
                  if (action.action === 'email') {
                    if (isDropdownItem) {
                      return (
                        <div key={idx} className="p-1">
                          <EmailButton
                            recordData={recordData}
                            objectName={objectName}
                            currentUser={getCurrentUser()}
                            variant="outline-primary"
                            size="sm"
                            className="w-100"
                            showTooltip={false}
                            onEmailSent={emailData => {
                              console.log('Email sent successfully:', emailData);
                              setToast({
                                show: true,
                                message: `Email sent successfully!`,
                                variant: 'success',
                              });
                              refreshRecordData();
                            }}
                          />
                        </div>
                      );
                    }

                    return (
                      <EmailButton
                        key={idx}
                        recordData={recordData}
                        objectName={objectName}
                        currentUser={getCurrentUser()}
                        variant="outline-primary"
                        size="sm"
                        onEmailSent={emailData => {
                          console.log('Email sent successfully:', emailData);
                          setToast({
                            show: true,
                            message: `Email sent successfully!`,
                            variant: 'success',
                          });
                          refreshRecordData();
                        }}
                      />
                    );
                  }

                  return null;
                };

                // Split actions into first 3 and the rest (one Blank Fields toggle max)
                let seenBlankFieldsToggle = false;
                const actions = recordData.header.actions.filter((action) => {
                  if (!isBlankFieldsToggleAction(action)) return true;
                  if (seenBlankFieldsToggle) return false;
                  seenBlankFieldsToggle = true;
                  return true;
                });
                const firstThreeActions = actions.slice(0, 3);
                const remainingActions = actions.slice(3);

                return (
                  <>
                    {/* First 3 actions as buttons */}
                    {firstThreeActions.map((action, idx) => renderActionButton(action, idx, false))}

                    {/* Remaining actions in dropdown */}
                    {remainingActions.length > 0 && (
                      <Dropdown className="d-inline-block">
                        <Dropdown.Toggle className="detail-action-button" id="actions-dropdown" style={{ marginRight: 0 }}>
                          <i className="fas fa-ellipsis-v"></i>
                          More
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ minWidth: '200px', padding: '4px' }}>
                          {remainingActions.map((action, idx) => renderActionButton(action, idx + 3, true))}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {hideEmbedActionBar &&
          recordData?.header?.actions?.some((a) => isBlankFieldsToggleAction(a)) && (
          <div className='sf-actions sf-actions--embed-only d-flex justify-content-end px-2 py-1'>
            {(() => {
              const blankAction = recordData.header.actions.find((a) =>
                isBlankFieldsToggleAction(a)
              );
              return blankAction
                ? renderBlankFieldsToggleButton(blankAction, 'embed-blank', false)
                : null;
            })()}
          </div>
        )}

        {/* Highlight Section - Only render if there are highlight fields */}
        {
          (() => {
            // Get task type for field visibility filtering
            const taskType =
              objectName === 'task'
                ? recordData?.details
                  ?.flatMap(s => s.fields)
                  ?.find(
                    f =>
                      f.fieldName === 'task_type' ||
                      f.fieldName === 'tasktype' ||
                      f.fieldName === 'type'
                  )?.value
                : null;

            // Get mail config type for highlight section (SMTP, Outlook, Google)
            const highlightMailConfigType =
              objectName === 'mail_config'
                ? (recordData?.highlightSection || [])
                  .find(
                    f =>
                      f?.fieldName === 'type' || f?.fieldName === 'Type'
                  )
                  ?.value ??
                recordData?.details
                  ?.flatMap(s => s.fields)
                  ?.find(
                    f =>
                      f.fieldName === 'type' || f.fieldName === 'Type'
                  )?.value
                : null;

          const filteredHighlightFields = filterDetailLayoutFields(
            recordData?.highlightSection || [],
            { taskType, mailConfigType: highlightMailConfigType }
          );

          // Only render if there are visible highlight fields
          if (!filteredHighlightFields || filteredHighlightFields.length === 0) {
            return null;
          }

          return (
            <div
              className='highlight-fields'
              style={{
                gridTemplateColumns: recordData?.highlightSection?.layoutColumns
                  ? `repeat(${recordData.highlightSection.layoutColumns}, 1fr)`
                  : 'repeat(6, 1fr)',
              }}
            >
              {filteredHighlightFields.map((field, idx) => (
                <React.Fragment
                  key={field.fieldName || `highlight-${idx}`}
                >
                  {renderDetailItem(field)}
                </React.Fragment>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Path Components - Render before details */}
      {!hideEmbedPath && (() => {
        // Collect all path fields from all sections and highlight section
        const allFields = [
          ...(recordData?.highlightSection || []),
          ...(recordData?.details || []).flatMap(section => section.fields || [])
        ];

        const pathFields = allFields.filter(field =>
          field.is_path_field === true &&
          field.picklistValues &&
          field.picklistValues.length > 0 &&
          (!blankFieldsMode || blankFieldsMode === 'all'
            ? true
            : blankFieldsMode === 'hide_blank'
              ? !isDetailFieldBlank(field)
              : isDetailFieldBlank(field))
        );

        if (pathFields.length > 0) {
          return (
            <div className="path-fields-container">
              {pathFields.map(pathField => {
                const pathFieldHistoryTracked =
                  fieldHistoryTrackedFieldNames != null &&
                  fieldHistoryTrackedFieldNames.has(pathField.fieldName) &&
                  !recordData?.is_history_object;
                const pathReadOnly =
                  recordData?.is_history_object ||
                  embedDisableEdit ||
                  !detailLayoutIncludesEditAction ||
                  pathField?.isReadonly === true ||
                  pathField?.is_readonly === true;
                return (
                  <PathComponent
                    key={pathField.fieldName}
                    field={{
                      id: pathField.fieldName,
                      field_name: pathField.fieldName,
                      field_label: pathField.label,
                      isReadonly: pathField?.isReadonly,
                      is_readonly: pathField?.is_readonly,
                    }}
                    readOnly={pathReadOnly}
                    currentValue={pathField.value}
                    onStageChange={(newValue) => handlePathStageChange(pathField.fieldName, newValue)}
                    picklistValues={pathField.picklistValues || []}
                    isUpdating={updatingPathField}
                    pathHistoryParentObject={pathFieldHistoryTracked ? objectName : null}
                    pathHistoryRecordId={pathFieldHistoryTracked ? id : null}
                    pathHistoryFieldName={pathFieldHistoryTracked ? pathField.fieldName : null}
                  />
                );
              })}
            </div>
          );
        }
        return null;
      })()}

      {/* Body */}
      <div style={{ overflowX: 'hidden', boxSizing: 'border-box', marginTop: '12px' }}>
        {(() => {
          // Get layout configuration from recordData
          const totalColumns = hideEmbedSideWidgets
            ? 1
            : recordData?.total_columns || 3;
          const detailColumnPosition = hideEmbedSideWidgets
            ? 1
            : recordData?.detail_column_position || 1;
          const widgets = hideEmbedSideWidgets
            ? []
            : (recordData?.widgets || []).filter((w) => {
              const t = String(w.widgetType || '').toLowerCase();
              if (t !== 'search') return true;
              const cfg = w.config || {};
              return cfg.show_in_detail_tabs === false;
            });
          const hasWidgets = widgets.length > 0;

          // Calculate column width based on total columns and column type
          const getColumnWidth = (isDetailColumn) => {
            if (totalColumns === 1) {
              // 1 column: full width
              return 12;
            } else if (totalColumns === 2) {
              // 2 columns: detail gets 70% (8.4 ≈ 8), widget gets 30% (3.6 ≈ 4)
              // Using Bootstrap grid: 8 for detail (66.67%), 4 for widget (33.33%)
              // For exact 70-30, we'll use custom width
              return isDetailColumn ? 8 : 4;
            } else {
              // 3 columns: each column gets 4 units (4-4-4 = 12) - equal split
              return 4;
            }
          };

          // Get column width for detail and widget columns
          const getColumnWidthValue = (isDetailColumn) => {
            if (totalColumns === 2) {
              // For 2 columns, use percentage-based widths for exact 70-30 split
              // Return object with custom styles and no lg prop
              return {
                customWidth: true,
                style: isDetailColumn
                  ? { flex: '0 0 70%', maxWidth: '70%', width: '70%' }
                  : { flex: '0 0 30%', maxWidth: '30%', width: '30%' }
              };
            }
            // For 1 or 3 columns, use Bootstrap grid
            return {
              customWidth: false,
              lg: getColumnWidth(isDetailColumn)
            };
          };

          // Determine which columns widgets should appear in
          const getWidgetColumns = () => {
            if (totalColumns === 1) {
              return [];
            }

            const widgetColumns = [];
            for (let i = 1; i <= totalColumns; i++) {
              if (i !== detailColumnPosition) {
                widgetColumns.push(i);
              }
            }
            return widgetColumns;
          };

          const widgetColumns = getWidgetColumns();

          // Filter widgets by their column position
          const getWidgetsForColumn = (columnNum) => {
            return widgets.filter(w => w.widgetColumnPosition === columnNum);
          };

          return (
            <Row style={{ width: '100%', margin: 0, marginLeft: 0, marginRight: 0 }}>
              {/* Render columns based on configuration */}
              {Array.from({ length: totalColumns }, (_, index) => {
                const columnNum = index + 1;
                const isDetailColumn = columnNum === detailColumnPosition;
                const columnWidgets = isDetailColumn ? [] : getWidgetsForColumn(columnNum);

                if (isDetailColumn) {
                  // Detail Layout Column
                  const detailWidth = getColumnWidthValue(true);
                  return (
                    <Col
                      key={`detail-${columnNum}`}
                      xs={12}
                      {...(detailWidth.customWidth ? {} : { lg: detailWidth.lg })}
                      style={{
                        padding: 0,
                        paddingLeft: columnNum === 1 ? 0 : '1rem',
                        paddingRight: columnNum === totalColumns ? 0 : '1rem',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        ...(detailWidth.customWidth ? detailWidth.style : {})
                      }}
                    >
                      <div className='left-panel'>
                        {/* Tabs */}
                        {(() => {
                          if (hideEmbedDetailsTabBar) {
                            return null;
                          }

                          const allTabs = [
                            { tabName: 'detailsTab', label: 'Details' },
                            ...normalizedCustomTabs.map(tab => ({ tabName: tab.tabName, label: tab.label })),
                            ...layoutDetailEmbedTabsForDisplay.map(tab => ({
                              tabName: `layoutEmbed:${tab.tab_name}`,
                              label: tab.tab_label
                            })),
                            ...detailTabsMergedForDisplay.map(tab => ({
                              tabName: tab.tabName,
                              label: tab.displayLabel || tab.tabName
                            }))
                          ];

                          // Use dynamic maxVisibleTabs calculated from container width
                          const visibleTabs = allTabs.slice(0, maxVisibleTabs);
                          const overflowTabs = allTabs.slice(maxVisibleTabs);



                          return (
                            <div className='tabs' ref={tabsContainerRef}>
                              {/* Offscreen measurement row: keeps overflow logic stable (no "More" flip-flop). */}
                              <div
                                ref={tabsMeasureRef}
                                style={{
                                  position: 'absolute',
                                  left: -99999,
                                  top: 0,
                                  height: 0,
                                  overflow: 'hidden',
                                  visibility: 'hidden',
                                  pointerEvents: 'none',
                                  display: 'flex',
                                  gap: 8,
                                  whiteSpace: 'nowrap',
                                }}
                                aria-hidden="true"
                              >
                                {allTabs.map((tab) => (
                                  <button
                                    key={`measure:${tab.tabName}`}
                                    ref={(el) => {
                                      if (el) tabMeasureButtonRefs.current.set(tab.tabName, el);
                                      else tabMeasureButtonRefs.current.delete(tab.tabName);
                                    }}
                                    type="button"
                                    className="tab-button"
                                  >
                                    {tab.label}
                                  </button>
                                ))}
                                <button
                                  ref={tabsMoreMeasureRef}
                                  type="button"
                                  className="tab-button"
                                >
                                  More
                                </button>
                              </div>
                              {/* Visible tabs */}
                              {visibleTabs.map(tab => (
                                <button
                                  key={tab.tabName}
                                  className={`tab-button ${activeTab === tab.tabName ? 'active' : ''}`}
                                  onClick={() => switchTab(tab.tabName)}
                                >
                                  {tab.label}
                                </button>
                              ))}

                              {/* Dropdown for overflow tabs - show when there are overflow tabs */}
                              {overflowTabs.length > 0 && (
                                <div
                                  ref={tabsDropdownRef}
                                  className="tabs-dropdown-wrapper"
                                  style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    zIndex: 1000,
                                    overflow: 'visible'
                                  }}
                                >
                                  <button
                                    type="button"
                                    className={`tab-button ${overflowTabs.some(t => activeTab === t.tabName) ? 'active' : ''}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('More button clicked, current state:', showTabsDropdown);
                                      const newState = !showTabsDropdown;
                                      console.log('Setting state to:', newState);
                                      setShowTabsDropdown(newState);
                                    }}
                                    style={{
                                      padding: '12px 16px',
                                      background: overflowTabs.some(t => activeTab === t.tabName) ? '#f4f6f9' : 'none',
                                      border: 'none',
                                      fontSize: '14px',
                                      cursor: 'pointer',
                                      fontWeight: 500,
                                      color: overflowTabs.some(t => activeTab === t.tabName) ? '#0176d3' : '#555',
                                      borderBottom: overflowTabs.some(t => activeTab === t.tabName) ? '3px solid #0176d3' : '3px solid transparent',
                                      boxShadow: 'none'
                                    }}
                                  >
                                    More <i className={`fas fa-chevron-${showTabsDropdown ? 'up' : 'down'} ms-1`}></i>
                                  </button>
                                  {/* Portal for dropdown menu to avoid z-index and overflow issues */}
                                  {showTabsDropdown && typeof document !== 'undefined' && (() => {
                                    // Calculate position for fixed positioning
                                    const buttonRect = tabsDropdownRef.current?.querySelector('button')?.getBoundingClientRect();

                                    if (!buttonRect) return null;

                                    const menuContent = (
                                      <div
                                        id="tabs-dropdown-portal"
                                        className="tabs-dropdown-menu"
                                        style={{
                                          position: 'fixed',
                                          top: `${buttonRect.bottom + 2}px`,
                                          left: `${buttonRect.left}px`,
                                          display: 'block',
                                          visibility: 'visible',
                                          opacity: 1,
                                          maxHeight: '400px',
                                          overflowY: 'auto',
                                          zIndex: 10050,
                                          minWidth: '200px',
                                          backgroundColor: '#fff',
                                          border: '1px solid #dee2e6',
                                          borderRadius: '0.375rem',
                                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                          marginTop: '0',
                                          padding: '4px 0',
                                          transform: 'none',
                                          willChange: 'auto'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                      >
                                        {overflowTabs.map(tab => (
                                          <button
                                            key={tab.tabName}
                                            type="button"
                                            className={`dropdown-item ${activeTab === tab.tabName ? 'active' : ''}`}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              switchTab(tab.tabName);
                                              setShowTabsDropdown(false);
                                            }}
                                            style={{
                                              display: 'block',
                                              width: '100%',
                                              padding: '10px 20px',
                                              textAlign: 'left',
                                              color: activeTab === tab.tabName ? '#0176d3' : '#212529',
                                              fontWeight: activeTab === tab.tabName ? 600 : 400,
                                              backgroundColor: activeTab === tab.tabName ? '#f4f6f9' : 'transparent',
                                              border: 'none',
                                              cursor: 'pointer',
                                              transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                              if (activeTab !== tab.tabName) {
                                                e.target.style.backgroundColor = '#f8f9fa';
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              if (activeTab !== tab.tabName) {
                                                e.target.style.backgroundColor = 'transparent';
                                              }
                                            }}
                                          >
                                            {tab.label}
                                          </button>
                                        ))}
                                      </div>
                                    );

                                    return createPortal(menuContent, document.body);
                                  })()}
                                </div>
                              )}

                            </div>
                          );
                        })()}

                        {/* Detail Sections */}
                        <div
                          className={`tab-content ${hideEmbedDetailsTabBar || activeTab === 'detailsTab' ? 'active' : ''
                            }`}
                        >
                          {recordData?.details?.map((section, sectionIndex) =>
                            renderSection(section, sectionIndex)
                          )}
                        </div>

                        {/* Related List Tabs */}
                        {normalizedCustomTabs.length > 0 && normalizedCustomTabs.map(tab => {
                          if (tab?.type !== 'component') {
                            return null;
                          }

                          if (!lazyDetailTabContentKeys.has(tab.tabName)) {
                            return (
                              <div
                                key={tab.tabName}
                                className={`tab-content ${activeTab === tab.tabName ? 'active' : ''}`}
                              />
                            );
                          }

                          // Built-in admin MFA recovery tab bypasses component_registry allowlist
                          const tabAllowlist =
                            tab?.component === 'UserMfaAdminTab'
                              ? null
                              : uiTabComponentAllowlist;
                          const { Component: TabComponent, blockedReason } = resolveRegisteredTabComponent(
                            tab?.component,
                            tabAllowlist
                          );
                          if (!TabComponent) {
                            const msg =
                              blockedReason === 'not_activated'
                                ? `Component "${tab?.component}" is not enabled in component_registry for this organization.`
                                : `Component "${tab?.component}" is not registered in the app.`;
                            return (
                              <div
                                key={tab.tabName}
                                className={`tab-content ${activeTab === tab.tabName ? 'active' : ''}`}
                              >
                                <div className="no-related-tabs">
                                  <p>{msg}</p>
                                </div>
                              </div>
                            );
                          }

                          const mappedTabProps = buildDetailCustomActionProps(tab, {
                            layoutObjectName: objectName,
                            getFieldValueForMapping: getRelationshipFilterValue,
                          });

                          return (
                            <div
                              key={tab.tabName}
                              className={`tab-content ${activeTab === tab.tabName ? 'active' : ''}`}
                            >
                              <TabComponent
                                recordData={recordData}
                                objectName={objectName}
                                recordId={id}
                                onRefresh={refreshRecordData}
                                {...mappedTabProps}
                              />
                            </div>
                          );
                        })}

                        {layoutDetailEmbedTabsForDisplay.map(tab => {
                          const tabKey = `layoutEmbed:${tab.tab_name}`;
                          const childId = resolveFieldAsRecordId(tab.parent_field);
                          const hideNestedActionBar = tab.hide.includes('actionbar');
                          const hideNestedSideWidgets = tab.hide.includes('sidewidgets');
                          const hideNestedRelated =
                            tab.hide.includes('relatedlist') ||
                            tab.hide.includes('relatedlists');
                          const hideNestedPath = tab.hide.includes('path');
                          const embedActivated = lazyDetailTabContentKeys.has(tabKey);
                          return (
                            <div
                              key={tabKey}
                              className={`tab-content ${activeTab === tabKey ? 'active' : ''
                                }`}
                            >
                              {embedActivated &&
                                (!childId ? (
                                  <div className='text-muted py-3 px-2 small'>
                                    No linked {tab.parent_object} record (
                                    <code>{tab.parent_field}</code> is empty).
                                  </div>
                                ) : (
                                  <DetailView
                                    key={`${tabKey}:${tab.parent_object}:${childId}`}
                                    objectName={tab.parent_object}
                                    id={childId}
                                    socket={socket}
                                    selectedWhatsAppSetting={selectedWhatsAppSetting}
                                    embedOptions={{
                                      hideActionBar: hideNestedActionBar,
                                      hideSideWidgets: hideNestedSideWidgets,
                                      hideRelatedLists: hideNestedRelated,
                                      hidePath: hideNestedPath,
                                      disableEdit: !tab.edit,
                                      isLayoutEmbed: true,
                                    }}
                                  />
                                ))}
                            </div>
                          );
                        })}

                        {detailTabsMergedForDisplay.length > 0 ? (
                          <RelatedTabsModal
                            tabs={detailTabsMergedForDisplay}
                            activeTab={activeTab}
                            onTabChange={switchTab}
                            onRefresh={refreshRecordData}
                            onRelatedListRowsRefresh={refreshActiveRelatedTabRowsOnly}
                            currentRecordId={id}
                            sourceObjectName={objectName}
                            recordData={recordData}
                            relatedTabLoadingById={lazyRelatedTabLoading}
                            onRelatedListTabRefresh={handleRelatedListTabRefresh}
                            getRelationshipFilterValue={getRelationshipFilterValue}
                          />
                        ) : (
                          recordData &&
                          !hideEmbedDetailsTabBar &&
                          layoutDetailEmbedTabsForDisplay.length === 0 &&
                          detailTabsMerged.length === 0 && (
                            <div className='no-related-tabs'>
                              <p>No related lists or search views configured for this record.</p>
                            </div>
                          )
                        )}
                      </div>
                    </Col>
                  );
                } else {
                  // Widget Column - Always render if it's a widget column, even if empty
                  // Always render widget columns to maintain layout structure
                  const widgetWidth = getColumnWidthValue(false);
                  return (
                    <Col
                      key={`widget-${columnNum}`}
                      xs={12}
                      {...(widgetWidth.customWidth ? {} : { lg: widgetWidth.lg })}
                      style={{
                        padding: 0,
                        paddingLeft: columnNum === 1 ? 0 : '1rem',
                        paddingRight: columnNum === totalColumns ? 0 : '1rem',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        ...(widgetWidth.customWidth ? widgetWidth.style : {})
                      }}
                    >
                      <div className='widgets-panel' style={{ width: '100%' }}>
                        {/* Widgets for this column */}
                        {columnWidgets.length > 0 && (
                          <div className="widget-column">
                            {layoutWidgetsMountReady ? (
                              columnWidgets.map(widget => (
                                <LayoutWidget
                                  key={widget.widgetId}
                                  widget={{ ...widget, recordData }}
                                  onRefresh={refreshRecordData}
                                  currentRecordId={id}
                                  sourceObjectName={objectName}
                                  onShowAll={handleWidgetShowAll}
                                />
                              ))
                            ) : (
                              <div
                                className="d-flex align-items-center justify-content-center py-4 text-muted small"
                                aria-busy="true"
                              >
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                                Loading widgets…
                              </div>
                            )}
                          </div>
                        )}

                        {/* Empty state - show nothing if no widgets */}
                        {columnWidgets.length === 0 && (
                          <div style={{ minHeight: '50px' }}></div>
                        )}
                      </div>
                    </Col>
                  );
                }
              })}
            </Row>
          );
        })()}
      </div>

      {/* Lead Convert Modal */}
      {objectName === 'lead' && (
        <LeadConvertModal
          show={showLeadConvertModal}
          onHide={() => setShowLeadConvertModal(false)}
          leadData={recordData}
          blockedByLeadStatus={statusHidesLeadConvert}
          onConvert={result => {
            if (result.success) {
              setToast({
                show: true,
                message: result.message,
                variant: 'success',
              });

              // Redirect based on configured preference
              const redirectPreference = result?.redirectPreference || 'opportunity';
              let targetObject = null;
              let targetId = null;

              // Determine redirect target based on preference
              if (result?.customObjects?.[redirectPreference]) {
                targetObject = redirectPreference;
                targetId = result.customObjects[redirectPreference];
              } else if (redirectPreference === 'opportunity' && result?.opportunity?.id) {
                targetObject = 'opportunity';
                targetId = result.opportunity.id;
              } else if (redirectPreference === 'account' && result?.account?.id) {
                targetObject = 'account';
                targetId = result.account.id;
              } else if (redirectPreference === 'contact' && result?.contact?.id) {
                targetObject = 'contact';
                targetId = result.contact.id;
              } else {
                // Fallback: try opportunity, then account, then contact
                if (result?.customObjects && Object.keys(result.customObjects).length > 0) {
                  const [firstCustomObject, firstCustomId] = Object.entries(result.customObjects)[0];
                  targetObject = firstCustomObject;
                  targetId = firstCustomId;
                } else if (result?.opportunity?.id) {
                  targetObject = 'opportunity';
                  targetId = result.opportunity.id;
                } else if (result?.account?.id) {
                  targetObject = 'account';
                  targetId = result.account.id;
                } else if (result?.contact?.id) {
                  targetObject = 'contact';
                  targetId = result.contact.id;
                }
              }

              if (targetObject && targetId) {
                // Use setTimeout to allow notification to show briefly before redirect
                setTimeout(() => {
                  window.location.href = `/view/${targetObject}/${targetId}`;
                }, 1500); // 1.5 second delay to show the success message
              }
            }
          }}
        />
      )
      }


      <Modal
        show={webComponentModal.show}
        onHide={closeWebComponentModal}
        centered
        size={webComponentModal.formConfig?.modalSize === 'xl' ? 'xl' : 'lg'}
      >
        <Modal.Header closeButton className={webComponentModal.modalTitle ? '' : 'border-0 pb-2'}>
          {webComponentModal.modalTitle ? (
            <Modal.Title>{webComponentModal.modalTitle}</Modal.Title>
          ) : (
            <Modal.Title visuallyHidden>Dialog</Modal.Title>
          )}
        </Modal.Header>
        <Modal.Body>
          {webComponentModal.loading ? <p className="mb-0 text-muted">Loading form…</p> : null}
          {webComponentModal.loadError ? (
            <p className="mb-0 text-danger">{webComponentModal.loadError}</p>
          ) : null}
          {!webComponentModal.loading && webComponentModal.formConfig ? (
            <DynamicForm
              key={webComponentModal.formKey}
              config={webComponentModal.formConfig}
              objectName={objectName}
              recordId={id}
              submitting={webComponentModal.submitting}
              onSubmit={handleWebComponentModalSubmit}
              result={webComponentModal.result}
              initialValues={webComponentModal.resolvedViewPageProps || undefined}
              onModalClose={closeWebComponentModal}
              submitFeedbackBanner={webComponentModal.submitFeedbackBanner}
            />
          ) : null}
        </Modal.Body>
      </Modal>

      <Modal
        show={customDetailActionFrame.show}
        onHide={() => setCustomDetailActionFrame({ show: false, url: '', title: '' })}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{customDetailActionFrame.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ minHeight: '60vh' }}>
          {customDetailActionFrame.url ? (
            <iframe
              title={customDetailActionFrame.title}
              src={customDetailActionFrame.url}
              style={{ width: '100%', height: '70vh', border: 'none' }}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            />
          ) : null}
        </Modal.Body>
      </Modal>

      <Modal
        show={customDetailActionComponent.show}
        onHide={() =>
          setCustomDetailActionComponent({
            show: false,
            title: '',
            Component: null,
            componentProps: null,
          })
        }
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{customDetailActionComponent.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {(() => {
            const Comp = customDetailActionComponent.Component;
            const props = customDetailActionComponent.componentProps;
            return Comp && props ? <Comp {...props} /> : null;
          })()}
        </Modal.Body>
      </Modal>

      <DetailSingleFieldEditModal
        show={Boolean(singleFieldEditName)}
        onHide={() => setSingleFieldEditName(null)}
        objectName={objectName}
        recordId={id}
        fieldName={singleFieldEditName || ''}
        onSaved={refreshDetailAfterSave}
        onOpenFullEdit={() => {
          setSingleFieldEditName(null);
          setShowEditModal(true);
        }}
      />

      {/* Edit Modal */}
      <EditLayoutModal
        show={showEditModal}
        onHide={handleEditModalClose}
        objectName={objectName}
        id={id}
        onSave={handleEditModalSave}
        onCancel={handleEditModalCancel}
        sourcePage='detail'
        onRefresh={refreshRecordData}
      />

      {/* Clone Modal */}
      <EditLayoutModal
        show={showCloneModal}
        onHide={handleCloneModalClose}
        objectName={objectName}
        id={id}
        isClone={true}
        onSave={handleCloneModalSave}
        onCancel={handleCloneModalCancel}
        sourcePage='detail'
        onRefresh={refreshRecordData}
      />

      {/* New record (record view picker + form in modal) */}
      <EditLayoutModal
        show={showNewRecordModal}
        onHide={handleNewRecordModalClose}
        objectName={objectName}
        id={null}
        onSave={handleNewRecordModalSave}
        onCancel={handleNewRecordModalCancel}
        sourcePage="detail"
      />

      <OpportunityProductWizard
        show={showProductWizard}
        onHide={handleProductWizardClose}
        opportunityId={id}
        opportunityCurrency={getRecordCurrencyOnly()}
        onSaveSuccess={handleProductWizardSaveSuccess}
        initialItems={existingLineItems}
        initialItemsLoading={loadingLineItems}
        lineItemWizardConfig={lineItemWizardConfigForModal}
      />
      <QuoteProductWizard
        show={showQuoteProductWizard}
        onHide={handleQuoteProductWizardClose}
        quoteId={id}
        quoteCurrency={getRecordCurrencyOnly()}
        onSaveSuccess={handleQuoteProductWizardSaveSuccess}
        lineItemWizardConfig={lineItemWizardConfigForModal}
      />
      <OrderProductWizard
        show={showOrderProductWizard}
        onHide={handleOrderProductWizardClose}
        orderId={id}
        orderCurrency={getRecordCurrencyOnly()}
        onSaveSuccess={handleOrderProductWizardSaveSuccess}
        lineItemWizardConfig={lineItemWizardConfigForModal}
      />
      {/* WhatsApp Chat Component */}
      <WatConnectChat
        socket={socket}
        selectedWhatsAppSetting={selectedWhatsAppSetting}
        isOpen={showWhatsAppChat}
        onToggle={() => setShowWhatsAppChat(!showWhatsAppChat)}
        onClose={() => setShowWhatsAppChat(false)}
        recipientName={defaultWhatsAppRecipientName}
        recipientPhone={whatsAppRecipientPhone || defaultWhatsAppRecipientPhone || ''}
      />
      {/* Universal success / error / info toasts (see AppToast + normalizeAppToastVariant) */}
      <AppToast
        show={toast.show}
        message={toast.message}
        variant={toast.variant}
        type={toast.type}
        sticky={toast.sticky === true}
        delay={toast.delay}
        messageFontSize={toast.messageFontSize}
        onClose={() => setToast((prev) => ({ ...prev, show: false, delay: undefined, sticky: undefined, type: undefined, messageFontSize: undefined }))}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={handleDeleteModalHide}
        onConfirm={confirmDeleteRecord}
        title="Confirm Delete"
        message="Are you sure you want to delete this record?"
        itemName={recordData?.header?.titleField ? getRecordFieldValue(recordData.header.titleField) : `${objectName} record`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isDeleting={isDeleting}
        resultMessage={deleteResultMessage}
        resultVariant={deleteResultVariant}
        showRelatedRecordsOptions
        relatedRecordsMode={relatedRecordsDeleteMode}
        onRelatedRecordsModeChange={setRelatedRecordsDeleteMode}
      />

      {docImageUploadModal && isEmbedTabActive && (
        <DocumentImageUploadModal
          show
          slots={docImageUploadModal.slots || []}
          generating={docImageUploadGenerating}
          onHide={() => {
            if (!docImageUploadGenerating) {
              setDocImageUploadModal(null);
              setGeneratingDocument(null);
            }
          }}
          onConfirm={handleDocImageUploadConfirm}
        />
      )}

      {/* Audience Modal */}
      <AudienceModal
        show={showAddAudience}
        onHide={() => setShowAddAudience(false)}
        objectName={objectName}
        recordData={recordData}
        getNameFromDetails={getNameFromDetails}
      />
      {/* Related List View Modal from Widget */}
      {showRelatedListViewModal && currentWidgetTab && currentWidgetTab.config?.target_object_name && (
        <RelatedListView
          show={showRelatedListViewModal}
          onHide={() => {
            setShowRelatedListViewModal(false);
            setCurrentWidgetTab(null);
            // Clean up window object
            delete window.currentModalTab;
          }}
          objectName={currentWidgetTab.config.target_object_name}
          parentField={currentWidgetTab.config?.target_field_name}
          parentId={id}
          parentFilterValue={getRelationshipFilterValue(currentWidgetTab.config?.source_field_name)}
          title={`All ${currentWidgetTab.tabName || 'Related Records'}`}
          allowedActions={currentWidgetTab.config?.actions || ['new', 'edit', 'clone', 'delete', 'bulk_delete', 'import', 'export']}
        />
      )}

      <TelecmiRecordingPlayerModal
        show={telecmiPlayer.show}
        filename={telecmiPlayer.filename}
        onHide={() => setTelecmiPlayer({ show: false, filename: null })}
      />

      {!isLayoutEmbedDetail && (
        <Modal
          show={lookupPreviewModal != null}
          onHide={() => setLookupPreviewModal(null)}
          size="xl"
          centered
          scrollable
          backdrop="static"
          aria-label="Record preview"
        >
          <Modal.Header
            closeButton={false}
            className="py-2 border-bottom d-flex flex-row align-items-center justify-content-end"
          >
            {lookupPreviewModal ? (
              <div className="d-inline-flex align-items-center gap-1 flex-shrink-0">
                <a
                  href={`/view/${encodeURIComponent(lookupPreviewModal.objectName)}/${encodeURIComponent(String(lookupPreviewModal.recordId))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                  onClick={() => {
                    dispatchLookupPreviewClose();
                  }}
                >
                  <i className="fas fa-external-link-alt" aria-hidden />
                  Open in new tab
                </a>
                <CloseButton
                  aria-label="Close"
                  onClick={() => setLookupPreviewModal(null)}
                />
              </div>
            ) : null}
          </Modal.Header>
          <Modal.Body className="p-0" style={{ maxHeight: 'min(85vh, 900px)', overflow: 'auto' }}>
            {lookupPreviewModal ? (
              <DetailView
                key={`lookup-preview-${lookupPreviewModal.objectName}-${lookupPreviewModal.recordId}`}
                objectName={lookupPreviewModal.objectName}
                id={lookupPreviewModal.recordId}
                socket={socket}
                selectedWhatsAppSetting={selectedWhatsAppSetting}
                embedOptions={{ isLayoutEmbed: true }}
              />
            ) : null}
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}

export default DetailView;
